import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { ChefHat, Plus, CheckCircle2, AlertTriangle, X, History, Search, Trash2, Flame, Snowflake, ChevronDown, ChevronRight, Pencil, Smartphone, Upload, Download, XCircle, Layers, BookOpen } from "lucide-react";
import api, { downloadServerFile, formatApiErrorDetail } from "../../lib/api";
import { formatItalianDateTime } from "../../lib/dateUtils";
import { Card, Button, Field, FieldHelp, Input, Select, inputCls, Spinner, StatusBadge, ConfirmDeleteDialog } from "../../components/ui-kit";
import ShelfLifeCategoryWizard from "../../components/ShelfLifeCategoryWizard";
import ConservationWizard, { refrigeratedText } from "../../components/ConservationWizard";
import { FROZEN_KEYS, DEFAULT_THAW_HOURS, DEFAULT_POST_THAW_DAYS, thawInstructions } from "../../lib/frozenLabel";
import AcademyTitleBadge from "../../academy/AcademyTitleBadge";
import RecipeLibraryModal, { SaveToLibraryDialog } from "../../components/RecipeLibraryModal";
import { POST_COOKING_MODES, MODE_BY_KEY, POST_COOKING_LABELS, POST_COOKING_DEFAULT_DAYS, SAME_DAY_KEYS, SURGELATO_INFO, RAW_STATE_LABELS, DATE_INDICATION_LABELS, modeNeedsBlast, processPayload } from "../../lib/postCookingModes";
import { PostCookingCards, PostCookingDetails, SuggestionBox, RawProcessDetails, ShelfLifeDetails, ModeChangeImpact, usePostCookingCheck } from "../../components/PostCookingPanel";

const ST = { bozza: "giallo", approvata: "verde", archiviata: "grigio" };
const STATUS_LABELS = { bozza: "Bozza", approvata: "Approvata", archiviata: "Archiviata" };
const RECIPE_CATEGORIES = ["Antipasti", "Primi piatti", "Secondi piatti", "Contorni", "Piatti unici", "Panificazione e pizzeria", "Gastronomia", "Preparazioni di macelleria", "Preparazioni di salumeria", "Preparazioni di pescheria", "Pasticceria e dessert", "Salse e condimenti", "Bevande e preparazioni liquide", "Altro"];
// Gestione del prodotto dopo la cottura (post_cooking_destination sulla
// ricetta): decide se in produzione va proposto l'abbattimento e i valori
// predefiniti di shelf life e conservazione. I giorni sono valori prudenziali
// del software, NON limiti di legge: vedi i testi informativi qui sotto.
const SHELF_LIFE_NOTE = "Valore prudenziale predefinito. La durata può variare in funzione della tipologia di alimento, del processo produttivo, delle condizioni di conservazione e dell’eventuale validazione della shelf life.";
// Le 8 modalità sono definite una volta sola in lib/postCookingModes.js.
const POST_COOKING_OPTIONS = POST_COOKING_MODES;
// Prodotti NON cotti (es. semifreddi, creme, basi di pasticceria): stesso
// campo post_cooking_destination, ma solo congelamento/surgelazione, che in
// produzione portano alla registrazione dell'abbattimento; "none" esiste
// solo nel modulo e viene salvato come null (nessun abbattimento).
const RAW_STORAGE_OPTIONS = [
  { key: "none", label: "Nessun abbattimento", desc: "Prodotto conservato fresco (es. in frigorifero): in produzione non viene proposto l’abbattimento." },
  { key: "frozen", label: "Abbattimento + congelamento (cella negativa)", desc: "Abbattuto subito dopo la preparazione e conservato a -18 °C o inferiore. In produzione verrà proposta la registrazione dell’abbattimento. Shelf life prudenziale 30 giorni, modificabile." },
  { key: "deep_frozen", label: "Abbattimento + surgelazione", desc: "Solo se il processo aziendale rispetta i requisiti di legge dei surgelati.", help: SURGELATO_INFO },
];
const RAW_BLAST_KEYS = ["frozen", "deep_frozen"];
const rawDestinationPayload = (key) => (RAW_BLAST_KEYS.includes(key) ? key : null);
const POST_COOKING_CONSERVATION = {
  ...Object.fromEntries(POST_COOKING_MODES.map((m) => [m.key, m.conservation])),
  refrigerated: refrigeratedText(0, 4),
  none: refrigeratedText(0, 4),
};
const PRUDENTIAL_DAYS = Object.values(POST_COOKING_DEFAULT_DAYS).filter((d) => d != null);
const SHELF_LIFE_HELP = {
  immediate: "Consumo o somministrazione immediata: la scadenza coincide con il giorno di produzione.",
  refrigerated: "2 giorni è un valore prudenziale predefinito del software, non un limite di legge. La normativa (Reg. CE 852/2004; Reg. CE 2073/2005, art. 3) affida all’operatore la definizione della shelf life in base al piano HACCP e agli eventuali studi di validazione: puoi indicare una durata diversa se prevista dalla tua procedura.",
  frozen: "30 giorni è un valore prudenziale predefinito del software, non un limite di legge: la normativa non fissa una durata massima per i prodotti congelati. La durata va definita dall’operatore nel piano HACCP (Reg. CE 852/2004; Reg. CE 2073/2005, art. 3) e non si applica la durata refrigerata di 2 giorni.",
  deep_frozen: "30 giorni è un valore prudenziale predefinito del software, non un limite di legge: la normativa non fissa una durata massima per i surgelati. La durata va definita dall’operatore (termine minimo di conservazione per i surgelati, D.Lgs. 110/1992) e non si applica la durata refrigerata di 2 giorni.",
  ambient: "Per i prodotti conservati a temperatura ambiente il software non propone una durata: la definisce il consulente in base alle caratteristiche del prodotto, al confezionamento e alle condizioni di conservazione (Reg. CE 852/2004; Reg. CE 2073/2005, art. 3). Per pane e prodotti della panetteria consumati entro 24 ore il TMC può non essere indicato in etichetta (Reg. UE 1169/2011, All. X), ma la durata resta definita.",
  hot_holding: "Mantenimento a caldo: valore predefinito «in giornata» (0 giorni), modificabile. Il tempo massimo di mantenimento si indica nei parametri della modalità.",
  temporary_display: "Esposizione temporanea: valore predefinito «in giornata» (0 giorni), modificabile. Il tempo massimo di esposizione si indica nei parametri della modalità.",
  custom: "Procedura personalizzata: la durata la definisce il consulente nella procedura; nessun valore predefinito.",
};
// Al cambio di destinazione si propongono i valori predefiniti, senza
// sovrascrivere quelli già personalizzati dall'utente.
// Le modalità senza valore prudenziale (temperatura ambiente, procedura
// personalizzata) lasciano la shelf life da definire.
const withPostCookingDefaults = (f, key) => {
  const isDefault = f.shelf_life_days === "" || PRUDENTIAL_DAYS.includes(Number(f.shelf_life_days));
  const days = POST_COOKING_DEFAULT_DAYS[key];
  return {
    ...f,
    post_cooking_destination: key,
    shelf_life_days: f.shelf_life_manual ? f.shelf_life_days : key === "immediate" || isDefault ? (days ?? "") : f.shelf_life_days,
    conservation: !f.conservation || Object.values(POST_COOKING_CONSERVATION).includes(f.conservation) ? (POST_COOKING_CONSERVATION[key] || "") : f.conservation,
  };
};

// Istruzioni di scongelamento per chi riceve il prodotto congelato (es. bar
// che lo mette in vetrina): finiscono nel campo "Uso" dell'etichetta. Sono
// facoltative e solo su scelta esplicita: un prodotto congelato non è sempre
// ceduto ad altre attività che lo scongelano (es. carne congelata di una
// macelleria), quindi nessuna dicitura viene aggiunta per impostazione.
const THAW_HELP = "Ore e giorni li decide l’operatore nel piano HACCP (Reg. CE 852/2004; Reg. CE 2073/2005, art. 3): non sono fissati dalla legge. I valori proposti (6 ore, 2 giorni) sono prudenziali e modificabili. In etichetta compaiono come istruzioni d’uso per chi riceve il prodotto, insieme a «Non ricongelare il prodotto scongelato».";
function ThawFields({ hours, days, onChange, testPrefix }) {
  const enabled = (hours !== "" && hours != null) || (days !== "" && days != null);
  const preview = thawInstructions(hours, days);
  const toggle = (on) => onChange(on ? DEFAULT_THAW_HOURS : "", on ? DEFAULT_POST_THAW_DAYS : "");
  return (
    <div className="mt-3 rounded-xl border border-sky-200 bg-white p-3" data-testid={`${testPrefix}-thaw`}>
      <label className="flex min-h-[44px] cursor-pointer items-start gap-3 text-sm font-semibold text-navy">
        <input type="checkbox" checked={enabled} onChange={(e) => toggle(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-sky-600" data-testid={`${testPrefix}-thaw-enabled`} />
        <span className="flex items-center gap-1.5">Il prodotto viene ceduto congelato ad altre attività che lo scongelano (es. bar che lo mettono in vetrina) <FieldHelp text={THAW_HELP} /></span>
      </label>
      {enabled ? (
        <>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            <Field label="Scongelamento in frigorifero (ore)"><Input type="number" min="1" value={hours ?? ""} onChange={(e) => onChange(e.target.value, days)} data-testid={`${testPrefix}-thaw-hours`} /></Field>
            <Field label="Dopo lo scongelamento consumare entro (giorni)"><Input type="number" min="1" value={days ?? ""} onChange={(e) => onChange(hours, e.target.value)} data-testid={`${testPrefix}-post-thaw-days`} /></Field>
          </div>
          {preview && <p className="mt-2 text-xs leading-relaxed text-slate-600">In etichetta, tra le istruzioni d’uso: <strong>{preview}</strong></p>}
        </>
      ) : <p className="mt-1 pl-8 text-xs text-slate-500">Non spuntare se il prodotto resta in azienda o è venduto congelato al consumatore: in etichetta non verrà aggiunta nessuna istruzione di scongelamento.</p>}
    </div>
  );
}
const thawPayload = (value) => (value === "" || value == null ? null : parseInt(value, 10));

// Sintesi numerica del ricettario, calcolata dalle ricette gia' caricate.
// Le versioni archiviate (sostituite da una versione piu' recente) non
// contano come ricette: sono solo indicate a parte.
const DESTINATION_SUMMARY = [
  { key: "immediate", label: "Consumo immediato" },
  { key: "refrigerated", label: "Cotte e refrigerate" },
  { key: "frozen", label: "Cotte e congelate" },
  { key: "deep_frozen", label: "Cotte e surgelate" },
  { key: "ambient", label: "Cotte, a temperatura ambiente" },
  { key: "hot_holding", label: "Cotte, mantenute a caldo" },
  { key: "temporary_display", label: "Cotte, esposizione temporanea" },
  { key: "custom", label: "Procedura personalizzata" },
  { key: "raw_frozen", label: "Non cotte, abbattute e congelate" },
  { key: "raw", label: "Non cotte" },
];
const destinationOf = (r) => (!r.is_cooked ? (RAW_BLAST_KEYS.includes(r.post_cooking_destination) ? "raw_frozen" : "raw") : r.post_cooking_destination || "refrigerated");

function SummaryTile({ label, value, hint, tone = "navy", active, onClick, testId }) {
  const tones = { navy: "text-navy", green: "text-emerald-700", red: "text-red-700", amber: "text-amber-700" };
  const content = (
    <>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 font-heading text-3xl font-bold ${tones[tone]}`}>{value}</div>
      {hint && <div className="mt-0.5 text-xs text-slate-500">{hint}</div>}
    </>
  );
  const cls = `min-h-[44px] rounded-xl border bg-white p-4 text-left ${active ? "border-navy ring-2 ring-navy/20" : "border-hairline"}`;
  return onClick
    ? <button type="button" onClick={onClick} aria-pressed={active} className={`${cls} transition-colors hover:bg-slate-50`} data-testid={testId}>{content}</button>
    : <div className={cls} data-testid={testId}>{content}</div>;
}

function RecipeSummary({ recipes, statusFilter, categoryFilter, onStatus, onCategory }) {
  const stats = useMemo(() => {
    const active = recipes.filter((r) => r.status !== "archiviata");
    const allergenCounts = {};
    active.forEach((r) => (r.allergens || []).forEach((key, index) => {
      const label = r.allergen_labels?.[index] || key;
      allergenCounts[label] = (allergenCounts[label] || 0) + 1;
    }));
    const categoryCounts = {};
    active.forEach((r) => { const c = r.category || "SENZA CATEGORIA"; categoryCounts[c] = (categoryCounts[c] || 0) + 1; });
    const destinationCounts = {};
    active.forEach((r) => { const d = destinationOf(r); destinationCounts[d] = (destinationCounts[d] || 0) + 1; });
    return {
      total: active.length,
      approved: active.filter((r) => r.status === "approvata").length,
      drafts: active.filter((r) => r.status === "bozza").length,
      archived: recipes.length - active.length,
      withAllergens: active.filter((r) => (r.allergens || []).length > 0).length,
      withShelfLifeEngine: active.filter((r) => r.sle_food_category_id).length,
      allergens: Object.entries(allergenCounts).sort((a, b) => b[1] - a[1]),
      categories: Object.entries(categoryCounts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "it")),
      destinations: DESTINATION_SUMMARY.map((d) => ({ ...d, count: destinationCounts[d.key] || 0 })).filter((d) => d.count > 0),
    };
  }, [recipes]);
  const pct = (n) => (stats.total ? Math.round((n / stats.total) * 100) : 0);
  const chip = "inline-flex min-h-[32px] items-center gap-1.5 rounded-full border px-3 py-1 text-xs";

  return (
    <section aria-labelledby="ricette-summary-title" className="space-y-3" data-testid="ricette-summary">
      <h2 id="ricette-summary-title" className="sr-only">Sintesi del ricettario</h2>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SummaryTile label="Ricette attive" value={stats.total} testId="summary-total"
          hint={stats.archived > 0 ? `+ ${stats.archived} ${stats.archived === 1 ? "versione archiviata" : "versioni archiviate"}` : "Versioni correnti"} />
        <SummaryTile label="Approvate" value={stats.approved} tone="green" hint={`${pct(stats.approved)}% · usabili in produzione`}
          active={statusFilter === "approvata"} onClick={() => onStatus("approvata")} testId="summary-approved" />
        <SummaryTile label="In bozza" value={stats.drafts} tone={stats.drafts ? "red" : "navy"} hint={stats.drafts ? "Da approvare" : "Nessuna da approvare"}
          active={statusFilter === "bozza"} onClick={() => onStatus("bozza")} testId="summary-drafts" />
        <SummaryTile label="Con allergeni" value={stats.withAllergens} tone="amber" hint={`${stats.total - stats.withAllergens} senza allergeni`} testId="summary-allergens" />
      </div>
      <Card className="grid gap-5 p-4 md:grid-cols-3" data-testid="ricette-summary-details">
        <div>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Gestione del prodotto</div>
          <ul className="space-y-1.5 text-sm">
            {stats.destinations.map((d) => (
              <li key={d.key} className="flex items-center justify-between gap-3"><span className="text-slate-600">{d.label}</span><span className="font-semibold text-navy">{d.count}</span></li>
            ))}
            <li className="flex items-center justify-between gap-3 border-t border-hairline pt-1.5"><span className="flex items-center gap-1 text-slate-600">Collegate all’Expert Engine<FieldHelp text="Ricette collegate a una categoria del DF Food Safety Expert Engine: a ogni abbattimento viene proposta in automatico una valutazione di shelf life." /></span><span className="font-semibold text-navy">{stats.withShelfLifeEngine}</span></li>
          </ul>
        </div>
        <div>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Allergeni più presenti</div>
          {stats.allergens.length ? (
            <div className="flex flex-wrap gap-1.5">
              {stats.allergens.map(([label, count]) => (
                <span key={label} className={`${chip} border-amber-200 bg-amber-50 text-amber-800`}>{label}<strong>{count}</strong></span>
              ))}
            </div>
          ) : <p className="text-sm text-slate-500">Nessun allergene nelle ricette attive.</p>}
        </div>
        <div>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Per categoria</div>
          <div className="flex flex-wrap gap-1.5">
            {stats.categories.map(([label, count]) => {
              const selectable = label !== "SENZA CATEGORIA";
              const active = categoryFilter === label;
              return selectable ? (
                <button key={label} type="button" onClick={() => onCategory(label)} aria-pressed={active}
                  className={`${chip} ${active ? "border-navy bg-navy text-white" : "border-hairline bg-white text-navy hover:bg-slate-50"}`}>
                  {label}<strong>{count}</strong>
                </button>
              ) : <span key={label} className={`${chip} border-hairline bg-slate-50 text-slate-500`}>{label}<strong>{count}</strong></span>;
            })}
          </div>
        </div>
      </Card>
    </section>
  );
}

const makePrefix = (name) => (name || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 18);
const searchable = (value) => (value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

export default function Ricette() {
  const { ctx, refreshDraftCount } = useOutletContext();
  const canManage = ["titolare", "super_admin"].includes(ctx.user.role);
  // Solo il consulente mentre è "entrato come cliente" (mai il titolare/cliente
  // stesso): l'import da file è una migrazione dati riservata al consulente.
  // Durante l'impersonazione ctx.user.role diventa quello del cliente (es.
  // "titolare"), quindi il flag giusto è impersonated_by, non role.
  const canImport = Boolean(ctx.user.impersonated_by);
  const [recipes, setRecipes] = useState(null);
  const [form, setForm] = useState(false);
  const [history, setHistory] = useState(null);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [justApprovedIds, setJustApprovedIds] = useState(new Set());
  const [deletingRecipe, setDeletingRecipe] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [selectedDraftIds, setSelectedDraftIds] = useState(new Set());
  const [bulkApproveOpen, setBulkApproveOpen] = useState(false);
  const [approvingRecipe, setApprovingRecipe] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [savingToLibrary, setSavingToLibrary] = useState(null);
  const [bulkCompleteOpen, setBulkCompleteOpen] = useState(false);
  const [bulkRawBlastOpen, setBulkRawBlastOpen] = useState(false);
  const toggleDraftSelected = (id) => setSelectedDraftIds((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleExpand = (id) => setExpanded((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const load = () => api.get("/app/catalog/recipes").then(({ data }) => setRecipes(data.recipes)).catch(() => setRecipes([])).finally(() => refreshDraftCount?.());
  useEffect(() => { load(); }, []);

  // Il pulsante rosso apre prima la sintesi di quanto configurato: si
  // approva da lì, con gli avvisi su eventuali dati mancanti.
  const approve = (r) => setApprovingRecipe(r);
  const afterApproved = async (r) => {
    setApprovingRecipe(null);
    setJustApprovedIds((current) => new Set(current).add(r.id));
    try {
      await new Promise((resolve) => setTimeout(resolve, 900));
      await load();
    } finally { setJustApprovedIds((current) => { const next = new Set(current); next.delete(r.id); return next; }); }
  };

  const confirmDeleteRecipe = async () => {
    if (!deletingRecipe) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/app/catalog/recipes/${deletingRecipe.id}`);
      toast.success("Ricetta eliminata");
      setDeletingRecipe(null);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDeleteBusy(false); }
  };

  const draftRecipes = useMemo(() => (recipes || []).filter((r) => r.status === "bozza"), [recipes]);
  const draftCount = draftRecipes.length;
  useEffect(() => {
    const validIds = new Set(draftRecipes.map((r) => r.id));
    setSelectedDraftIds((current) => {
      const next = new Set([...current].filter((id) => validIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [draftRecipes]);

  const bulkApprove = () => { if (selectedDraftIds.size > 0) setBulkApproveOpen(true); };
  const categoriesInUse = useMemo(() => [...new Set((recipes || []).map((r) => r.category).filter(Boolean))].sort((a, b) => a.localeCompare(b, "it")), [recipes]);
  const filteredRecipes = useMemo(() => (recipes || []).filter((r) =>
    (!search || searchable(r.name).includes(searchable(search)) || searchable(r.prefix).includes(searchable(search))) &&
    (!categoryFilter || r.category === categoryFilter) &&
    (!statusFilter || r.status === statusFilter)
  ), [recipes, search, categoryFilter, statusFilter]);
  const filtersActive = search || categoryFilter || statusFilter;
  const clearFilters = () => { setSearch(""); setCategoryFilter(""); setStatusFilter(""); };

  return (
    <div className="space-y-6" data-testid="ricette-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div><p className="text-xs uppercase tracking-[0.18em] text-slate-400 font-body">Produzione</p><div className="flex flex-wrap items-center gap-2"><h1 className="font-heading text-3xl font-bold">Ricette</h1><AcademyTitleBadge /></div></div>
        {(canManage || canImport) && (
          <div className="flex flex-wrap gap-2">
            {canImport && <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="ricetta-import-open"><Upload className="w-4 h-4" /> Importa ricette</Button>}
            {canManage && <Button variant="outline" onClick={() => setBulkRawBlastOpen(true)} data-testid="ricette-bulk-raw-blast-open"><Snowflake className="w-4 h-4" /> Abbattimento in blocco</Button>}
            {canManage && <Button variant="primary" onClick={() => setForm(true)} data-testid="ricetta-new"><Plus className="w-4 h-4" /> Nuova ricetta</Button>}
          </div>
        )}
      </div>

      {canManage && (
        <Card className="overflow-hidden border-2 border-blue-200 bg-gradient-to-r from-blue-50 via-white to-emerald-50 p-0" data-testid="recipe-library-card">
          <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4"><div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-blue-600 text-white shadow-sm"><BookOpen className="h-6 w-6" /></div><div><div className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-700">Inserimento rapido consigliato</div><h3 className="mt-1 font-heading text-xl font-semibold text-navy">Scegli ricette da archivio</h3><p className="mt-1 max-w-2xl text-sm text-slate-600">Ricette tipo per pizzeria, pasticceria, ristorante, macelleria e altre attività. Spunta quelle che servono: entrano in bozza con i soli ingredienti, poi le adatti e le approvi.</p></div></div>
            <Button variant="success" className="w-full sm:w-auto sm:shrink-0" onClick={() => setLibraryOpen(true)} data-testid="open-recipe-library"><BookOpen className="h-4 w-4" /> Apri archivio ricette</Button>
          </div>
        </Card>
      )}

      {draftCount > 0 && (
        <Card className="border-2 border-red-300 bg-red-50 p-4" role="alert" data-testid="ricette-draft-reminder">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 shrink-0 text-red-600" />
            <div className="text-sm text-red-800">
              <strong>{draftCount} {draftCount === 1 ? "ricetta in bozza" : "ricette in bozza"}</strong> — {draftCount === 1 ? "non può essere usata" : "non possono essere usate"} in produzione finché non {draftCount === 1 ? "la approvi" : "le approvi"}. Il pulsante rosso lampeggiante sulla scheda indica quali.
            </div>
          </div>
          {canManage && draftCount > 1 && (
            <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-red-200 pt-3">
              <label className="flex items-center gap-2 text-xs font-medium text-red-800">
                <input
                  type="checkbox"
                  checked={selectedDraftIds.size > 0 && selectedDraftIds.size === draftRecipes.length}
                  onChange={(e) => setSelectedDraftIds(e.target.checked ? new Set(draftRecipes.map((r) => r.id)) : new Set())}
                  data-testid="ricette-draft-select-all"
                />
                Seleziona tutte
              </label>
              <Button
                variant="danger"
                disabled={selectedDraftIds.size === 0}
                onClick={bulkApprove}
                data-testid="ricette-approve-selected"
              >
                <CheckCircle2 className="w-4 h-4" /> Approva selezionate ({selectedDraftIds.size})
              </Button>
              <Button
                variant="primary"
                disabled={selectedDraftIds.size === 0}
                onClick={() => setBulkCompleteOpen(true)}
                data-testid="ricette-bulk-complete-open"
              >
                <Layers className="w-4 h-4" /> Compila e approva in blocco ({selectedDraftIds.size})
              </Button>
              <p className="w-full text-xs text-red-700">Se le ricette selezionate seguono lo stesso processo, "Compila e approva in blocco" ti fa rispondere alle domande (conservazione, abbattimento, cottura) una sola volta per tutte.</p>
            </div>
          )}
        </Card>
      )}

      {!recipes ? <Spinner /> : recipes.length === 0 ? (
        <Card className="p-10 text-center text-slate-400"><ChefHat className="w-10 h-10 mx-auto mb-3 opacity-40" />Nessuna ricetta configurata.</Card>
      ) : (
        <>
          <RecipeSummary recipes={recipes} statusFilter={statusFilter} categoryFilter={categoryFilter}
            onStatus={(value) => setStatusFilter((current) => current === value ? "" : value)}
            onCategory={(value) => setCategoryFilter((current) => current === value ? "" : value)} />

          <Card className="p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Cerca ricetta per nome o prefisso lotto…"
                  aria-label="Cerca ricetta"
                  className={`${inputCls} pl-10 pr-10`}
                  data-testid="ricette-search"
                />
                {search && (
                  <button type="button" onClick={() => setSearch("")} className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-navy" aria-label="Cancella ricerca" data-testid="ricette-search-clear">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 sm:flex sm:shrink-0">
                <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="sm:w-48" aria-label="Filtra per categoria" data-testid="ricette-filter-category">
                  <option value="">Tutte le categorie</option>
                  {categoriesInUse.map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
                <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="sm:w-40" aria-label="Filtra per stato" data-testid="ricette-filter-status">
                  <option value="">Tutti gli stati</option>
                  {Object.entries(STATUS_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </Select>
              </div>
            </div>
            {filtersActive && (
              <div className="mt-3 flex items-center justify-between gap-3 text-xs text-slate-500">
                <span>{filteredRecipes.length} {filteredRecipes.length === 1 ? "ricetta trovata" : "ricette trovate"} su {recipes.length}</span>
                <button type="button" onClick={clearFilters} className="font-medium text-navy underline underline-offset-4">Azzera filtri</button>
              </div>
            )}
          </Card>

          {filteredRecipes.length === 0 ? (
            <Card className="p-10 text-center text-slate-400"><Search className="w-10 h-10 mx-auto mb-3 opacity-40" />Nessuna ricetta corrisponde ai filtri.</Card>
          ) : (
            <Card className="divide-y divide-hairline overflow-hidden p-0" data-testid="ricette-list">
              {filteredRecipes.map((r) => {
                const isOpen = expanded.has(r.id);
                return (
                  <div key={r.id} data-testid={`ricetta-${r.id}`}>
                    <div className="flex items-start gap-2 px-4 py-3 hover:bg-slate-50">
                      {canManage && r.status === "bozza" && (
                        <input
                          type="checkbox"
                          className="mt-3.5 shrink-0"
                          checked={selectedDraftIds.has(r.id)}
                          onChange={() => toggleDraftSelected(r.id)}
                          aria-label={`Seleziona ${r.name} per approvazione multipla`}
                          data-testid={`ricetta-select-${r.id}`}
                        />
                      )}
                      <button type="button" onClick={() => toggleExpand(r.id)} className="flex min-w-0 flex-1 items-start gap-3 text-left" data-testid={`ricetta-row-${r.id}`} aria-expanded={isOpen}>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-heading font-semibold text-navy break-words">{r.name?.toUpperCase()}</span>
                            {r.is_semi_finished && <SemiBadge />}
                            <span className="text-xs text-slate-400">v{r.version}</span>
                            <StatusBadge status={ST[r.status]}>{STATUS_LABELS[r.status] || r.status}</StatusBadge>
                            {r.intake_source && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700 ring-1 ring-indigo-200" data-testid={`ricetta-intake-badge-${r.id}`}>
                                <Smartphone className="h-3 w-3" /> Da modulo cliente
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-slate-500">
                            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-slate-600">{r.prefix}</span>
                            {r.category && <span>{r.category}</span>}
                            <span>{r.ingredients?.length || 0} ingredienti</span>
                            {r.shelf_life_days != null && <span>{r.shelf_life_days} gg</span>}
                            {r.core_temp != null && <span>{r.core_temp} °C</span>}
                            {r.intake_client_added_at && <span className="text-indigo-600">Inserita dal cliente il {formatItalianDateTime(r.intake_client_added_at)}</span>}
                            {r.allergen_labels?.map((a) => <span key={a} className="rounded-full bg-red-50 px-1.5 py-0.5 text-red-700 ring-1 ring-red-100">{a}</span>)}
                          </div>
                        </div>
                      </button>
                      <div className="flex shrink-0 items-center gap-1.5">
                        {canManage && r.status === "bozza" && (
                          <Button
                            variant={justApprovedIds.has(r.id) ? "success" : "danger"}
                            className={justApprovedIds.has(r.id) ? "" : "animate-pulse ring-2 ring-red-300 ring-offset-1"}
                            disabled={justApprovedIds.has(r.id)}
                            onClick={() => approve(r)}
                            data-testid={`ricetta-approve-${r.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4" /> <span className="hidden sm:inline">{justApprovedIds.has(r.id) ? "Approvata!" : "Approva"}</span>
                          </Button>
                        )}
                        <button type="button" onClick={() => toggleExpand(r.id)} aria-label={isOpen ? "Comprimi dettagli" : "Espandi dettagli"} className="flex h-11 shrink-0 items-center gap-1 rounded-lg px-2 text-xs font-medium text-slate-400 hover:bg-slate-100 hover:text-navy">
                          {isOpen ? "Chiudi" : "Apri"} <ChevronRight className={`h-4 w-4 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                        </button>
                      </div>
                    </div>

                    {isOpen && (
                      <div className="border-t border-hairline bg-slate-50/60 px-4 py-4">
                        {r.trace_labels?.length > 0 && (
                          <div className="mb-3 text-xs text-amber-700 font-body">Può contenere tracce di: {r.trace_labels.join(", ")}</div>
                        )}
                        <div className="grid grid-cols-3 gap-2 rounded-lg border border-hairline bg-white p-3 text-center">
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-400">Conservazione</div>
                            {!r.conservation && r.status === "approvata" ? (
                              <div className="mt-0.5 flex items-center justify-center gap-1 text-xs font-semibold text-red-600" data-testid={`ricetta-missing-conservation-${r.id}`}>
                                <AlertTriangle className="h-3 w-3 shrink-0" /> Mancante
                              </div>
                            ) : (
                              <div className="mt-0.5 break-words text-xs font-medium text-navy">{r.conservation || "—"}</div>
                            )}
                          </div>
                          <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Durata</div><div className="mt-0.5 text-xs font-medium text-navy">{r.shelf_life_days != null ? `${r.shelf_life_days} gg` : "—"}</div></div>
                          <div><div className="text-[10px] uppercase tracking-wider text-slate-400">T° cuore</div><div className="mt-0.5 text-xs font-medium text-navy">{r.core_temp != null ? `${r.core_temp} °C` : "—"}</div></div>
                        </div>
                        {r.ingredients?.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-hairline">
                            <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5">Ingredienti utilizzati nella ricetta</div>
                            <ul className="space-y-0.5" data-testid={`ricetta-ingredienti-${r.id}`}>
                              {r.ingredients.map((ing, idx) => (
                                <li key={idx} className="flex items-center justify-between gap-2 text-sm">
                                  <span className="min-w-0 truncate text-navy font-body">{ing.name || ing.ingredient_id}</span>
                                  <span className="shrink-0 text-xs text-slate-400">{ing.qty != null ? `${ing.qty} ${ing.unit || ""}` : ""}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <button onClick={() => setHistory(r)} className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-navy font-body" data-testid={`ricetta-history-${r.id}`}>
                          <History className="w-3.5 h-3.5" /> Storico versioni (v{r.version})
                        </button>
                        {canImport && !r.library_id && r.status !== "archiviata" && (
                          <button onClick={() => setSavingToLibrary(r)} className="mt-3 ml-4 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-navy font-body" data-testid={`ricetta-save-library-${r.id}`}>
                            <BookOpen className="w-3.5 h-3.5" /> Salva nell’archivio DF
                          </button>
                        )}
                        {canManage && (
                          <div className="mt-4 pt-4 border-t border-hairline flex flex-wrap gap-2">
                            <Button variant="accent" className="flex-1" onClick={() => setForm(r)} data-testid={`ricetta-edit-${r.id}`}><Pencil className="w-4 h-4" /> Modifica (nuova versione)</Button>
                            {r.status !== "archiviata" && (
                              <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => setDeletingRecipe(r)} aria-label={`Elimina ${r.name}`} data-testid={`ricetta-delete-${r.id}`}>
                                <Trash2 className="w-4 h-4" /> Elimina
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </Card>
          )}
        </>
      )}

      {form && <RecipeForm edit={typeof form === "object" ? form : null} onClose={() => setForm(false)} onDone={() => { setForm(false); load(); }} />}
      {history && <VersionHistory recipe={history} onClose={() => setHistory(null)} />}
      {savingToLibrary && <SaveToLibraryDialog recipe={savingToLibrary} onClose={() => setSavingToLibrary(null)} />}
      {libraryOpen && <RecipeLibraryModal onClose={() => setLibraryOpen(false)} onDone={() => { setLibraryOpen(false); load(); }} />}
      {importOpen && <RecipeImportModal onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }} />}
      {approvingRecipe && (
        <ApproveRecipeDialog
          recipe={approvingRecipe}
          onClose={() => setApprovingRecipe(null)}
          onEdit={() => { setForm(approvingRecipe); setApprovingRecipe(null); }}
          onApproved={afterApproved}
        />
      )}
      {bulkApproveOpen && (
        <BulkApproveDialog
          recipes={draftRecipes.filter((r) => selectedDraftIds.has(r.id))}
          onClose={() => setBulkApproveOpen(false)}
          onDone={() => { setBulkApproveOpen(false); setSelectedDraftIds(new Set()); load(); }}
        />
      )}
      {bulkRawBlastOpen && (
        <BulkRawBlastModal
          recipes={(recipes || []).filter((r) => r.status === "approvata" && !r.is_cooked)}
          onClose={() => setBulkRawBlastOpen(false)}
          onDone={() => { setBulkRawBlastOpen(false); load(); }}
        />
      )}
      {bulkCompleteOpen && (
        <BulkCompleteModal
          recipes={draftRecipes.filter((r) => selectedDraftIds.has(r.id))}
          onClose={() => setBulkCompleteOpen(false)}
          onDone={() => { setBulkCompleteOpen(false); setSelectedDraftIds(new Set()); load(); }}
        />
      )}
      <ConfirmDeleteDialog
        open={!!deletingRecipe}
        title={deletingRecipe?.status === "bozza" ? "Eliminare definitivamente questa ricetta?" : "Eliminare questa ricetta approvata?"}
        description={deletingRecipe && (deletingRecipe.status === "bozza"
          ? <>La ricetta in bozza <strong>{deletingRecipe.name}</strong> verrà eliminata. Questa operazione non può essere annullata.</>
          : <>La ricetta <strong>{deletingRecipe.name}</strong> e tutte le sue versioni spariranno dall’elenco e non si potranno più usare per nuove produzioni; le sue programmazioni automatiche verranno disattivate. Lotti, etichette e tracciabilità delle produzioni già fatte restano conservati.</>)}
        busy={deleteBusy}
        onCancel={() => setDeletingRecipe(null)}
        onConfirm={confirmDeleteRecipe}
      />
    </div>
  );
}

const ING_MAP = (arr) => Object.fromEntries((arr || []).map((i) => [i.ingredient_id, i]));

function computeDiff(prev, curr) {
  const p = ING_MAP(prev.ingredients), c = ING_MAP(curr.ingredients);
  const added = (curr.ingredients || []).filter((i) => !p[i.ingredient_id]).map((i) => i.name || i.ingredient_id);
  const removed = (prev.ingredients || []).filter((i) => !c[i.ingredient_id]).map((i) => i.name || i.ingredient_id);
  const qtyChanged = (curr.ingredients || []).filter((i) => p[i.ingredient_id] && String(p[i.ingredient_id].qty) !== String(i.qty))
    .map((i) => `${i.name || i.ingredient_id}: ${p[i.ingredient_id].qty ?? "—"} → ${i.qty ?? "—"} ${i.unit || ""}`);
  const fields = [];
  const F = [["conservation", "Conservazione"], ["shelf_life_days", "Durata (gg)"], ["core_temp", "T° cuore"], ["post_cooking_destination", "Dopo la cottura"], ["consumption", "Consumo"], ["cooling", "Abbattimento"]];
  F.forEach(([k, label]) => { if (String(prev[k] ?? "") !== String(curr[k] ?? "")) fields.push(`${label}: ${prev[k] ?? "—"} → ${curr[k] ?? "—"}`); });
  return { added, removed, qtyChanged, fields, empty: !added.length && !removed.length && !qtyChanged.length && !fields.length };
}

function VersionHistory({ recipe, onClose }) {
  const [vers, setVers] = useState(null);
  useEffect(() => { api.get(`/app/catalog/recipes/${recipe.id}/versions`).then(({ data }) => setVers(data.versions || [])).catch(() => setVers([])); }, [recipe.id]);
  const fmt = (d) => formatItalianDateTime(d);
  return (
    <div className="fixed inset-0 z-50 grid place-items-end sm:place-items-start p-0 sm:p-6" data-testid="ricetta-history-modal">
      <div className="absolute inset-0 bg-navy/50" onClick={onClose} />
      <Card className="relative w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto p-6 rounded-b-none sm:rounded-xl">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading text-xl font-semibold flex items-center gap-2"><History className="w-5 h-5 text-navy" /> Storico versioni</h3>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-slate-400 mb-4 font-body">{recipe.name} · prefisso {recipe.prefix}</p>
        {vers === null ? <Spinner /> : vers.length === 0 ? <p className="text-slate-400 text-sm">Nessuna versione.</p> : (
          <div className="space-y-4">
            {vers.map((v, idx) => {
              const older = vers[idx + 1];
              const diff = older ? computeDiff(older, v) : null;
              return (
                <div key={v.id} className="rounded-xl border border-hairline p-4" data-testid={`version-${v.version}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-heading font-semibold text-navy">Versione {v.version} <span className="text-xs font-normal text-slate-400">· {v.status}</span></div>
                    <div className="text-xs text-slate-400 font-body">{fmt(v.created_at)} · {v.created_by_name || "—"}</div>
                  </div>
                  {v.allergen_labels?.length > 0 && <div className="mt-1.5 text-xs text-red-700">Allergeni: {v.allergen_labels.join(", ")}</div>}
                  {v.ingredients?.length > 0 && (
                    <ul className="mt-2 space-y-0.5">
                      {v.ingredients.map((ing, i) => (
                        <li key={i} className="flex justify-between text-sm"><span className="text-navy font-body">{ing.name || ing.ingredient_id}</span><span className="text-xs text-slate-400">{ing.qty != null ? `${ing.qty} ${ing.unit || ""}` : ""}</span></li>
                      ))}
                    </ul>
                  )}
                  {diff && (
                    <div className="mt-3 pt-3 border-t border-hairline text-xs font-body" data-testid={`version-diff-${v.version}`}>
                      <div className="uppercase tracking-wider text-slate-400 mb-1">Modifiche rispetto a v{older.version}</div>
                      {diff.empty ? <div className="text-slate-400">Nessuna modifica sostanziale</div> : (
                        <ul className="space-y-0.5">
                          {diff.added.map((a, i) => <li key={`a${i}`} className="text-emerald-700">+ Aggiunto: {a}</li>)}
                          {diff.removed.map((a, i) => <li key={`r${i}`} className="text-red-600">− Rimosso: {a}</li>)}
                          {diff.qtyChanged.map((a, i) => <li key={`q${i}`} className="text-amber-700">~ Quantità: {a}</li>)}
                          {diff.fields.map((a, i) => <li key={`f${i}`} className="text-navy">~ {a}</li>)}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------------- sintesi prima dell'approvazione ----------------
const DESTINATION_LABELS = POST_COOKING_LABELS;
const RAW_DESTINATION_LABELS = Object.fromEntries(RAW_STORAGE_OPTIONS.map((o) => [o.key, o.label]));
const destinationLabel = (r) => (r.is_cooked ? DESTINATION_LABELS : RAW_DESTINATION_LABELS)[r.post_cooking_destination];
// Parole attese nella dicitura di conservazione per ogni destinazione: se
// mancano, la conservazione scritta probabilmente non è coerente con la scelta.
const DESTINATION_CONSERVATION_HINTS = {
  immediate: /GIORNATA|IMMEDIAT|SUBITO/,
  refrigerated: /FRIGO|REFRIGER/,
  frozen: /CONGEL|SURGEL|-18/,
  deep_frozen: /SURGEL|-18/,
  ambient: /AMBIENTE|ASCIUTT|FRESCO|RIPARO/,
  hot_holding: /CALDO|GIORNATA/,
  temporary_display: /AMBIENTE|ESPOST|ENTRO|GIORNATA/,
};
const recipeNeedsBlast = (r) => (r.is_cooked ? modeNeedsBlast(r.post_cooking_destination, r.post_cooking_process) : RAW_BLAST_KEYS.includes(r.post_cooking_destination));
const sameDayRecipe = (r) => r.is_cooked && SAME_DAY_KEYS.includes(r.post_cooking_destination) && r.post_cooking_destination !== "custom" && r.shelf_life_days === 0;

// blocking = il server rifiuta l'approvazione; gli altri sono avvisi.
function recipeApprovalIssues(r, { hasShelfLife }) {
  const issues = [];
  const conservation = (r.conservation || "").trim().toUpperCase();
  if (!conservation) issues.push({ blocking: true, text: "Manca la modalità di conservazione: senza questa la ricetta non può essere approvata." });
  const ingredients = r.ingredients || [];
  if (!ingredients.length) issues.push({ text: "Nessun ingrediente collegato: allergeni e lotti delle materie prime non possono essere calcolati." });
  const noQty = ingredients.filter((i) => i.qty == null || i.qty === "");
  if (ingredients.length && noQty.length) issues.push({ text: `Quantità non indicata per ${noQty.length === ingredients.length ? "tutti gli ingredienti" : noQty.map((i) => i.name || i.ingredient_id).join(", ")}: servono per QUID e dichiarazione nutrizionale.` });
  if (r.is_cooked && !r.post_cooking_destination) issues.push({ text: "Non è indicato come sarà gestito il prodotto dopo la cottura: in produzione verrà trattato come refrigerato (raffreddamento rapido proposto, shelf life indicata sulla ricetta)." });
  if (r.is_cooked && !r.post_cooking_destination && DESTINATION_CONSERVATION_HINTS.frozen.test(conservation)) issues.push({ text: "La conservazione indica un prodotto congelato/surgelato ma non è scelta la gestione dopo la cottura: con «Correggi» indica «congelamento» o «surgelazione», altrimenti resta la shelf life refrigerata della ricetta." });
  const hint = DESTINATION_CONSERVATION_HINTS[r.is_cooked || RAW_BLAST_KEYS.includes(r.post_cooking_destination) ? r.post_cooking_destination : ""];
  if (conservation && hint && !hint.test(conservation)) issues.push({ text: `La dicitura di conservazione non sembra coerente con la gestione scelta dopo la ${r.is_cooked ? "cottura" : "preparazione"} («${destinationLabel(r)}»). Verificala.` });
  if (r.shelf_life_days == null && !(hasShelfLife && r.sle_food_category_id)) issues.push({ text: "Shelf life non indicata: in etichetta non comparirà una data di scadenza." });
  if (r.shelf_life_manual && r.shelf_life_days > MANUAL_SHELF_LIFE_LAB_DAYS) issues.push({ text: `Scadenza impostata dal cliente di ${r.shelf_life_days} giorni: oltre 5 giorni va dimostrata con analisi di laboratorio di shelf life.` });
  if (hasShelfLife && !r.sle_food_category_id && !r.shelf_life_manual) issues.push({ text: "Nessuna categoria del DF Food Safety Expert Engine collegata: la shelf life resta il valore fisso indicato, senza calcolo automatico all'abbattimento." });
  const mode = r.is_cooked ? MODE_BY_KEY[r.post_cooking_destination] : null;
  if (mode?.assessment && (!(r.haccp_assessment?.motivation || "").trim() || (r.haccp_assessment?.mode && r.haccp_assessment.mode !== r.post_cooking_destination))) issues.push({ blocking: true, text: `«${mode.label}» richiede la motivazione della valutazione HACCP: completala con «Correggi» (sezione Valutazione HACCP). Finché manca, la ricetta resta in bozza.` });
  if (r.is_cooked && r.post_cooking_destination === "deep_frozen" && !r.post_cooking_process?.requirements_verified) issues.push({ text: "Surgelazione: non risulta spuntata la verifica dei requisiti del processo (D.Lgs. 110/1992)." });
  const check = r.post_cooking_check;
  if (r.is_cooked && check?.flags?.length && ["ambient", "temporary_display"].includes(r.post_cooking_destination)) issues.push({ text: `Questa ricetta contiene ingredienti che richiedono una valutazione specifica delle condizioni di conservazione (${check.flags.map((f) => f.text).join("; ")}).` });
  if (r.is_cooked && check?.suggestion && r.post_cooking_destination && check.suggestion !== r.post_cooking_destination) issues.push({ text: `Il software suggeriva «${DESTINATION_LABELS[check.suggestion]}»: è stata scelta un’altra modalità, verifica che sia motivata.` });
  return issues;
}

// Oltre questa durata la scadenza decisa dal cliente va supportata da analisi
// di laboratorio (studi di shelf life): solo avviso, il salvataggio resta libero.
const MANUAL_SHELF_LIFE_LAB_DAYS = 5;

function ManualShelfLifeToggle({ checked, days, onChange }) {
  const overLimit = checked && Number(days) > MANUAL_SHELF_LIFE_LAB_DAYS;
  return (
    <div className="mt-3 space-y-2" data-testid="ricetta-shelf-life-manual">
      <label className={`flex min-h-[44px] cursor-pointer items-start gap-3 rounded-xl border-2 p-3 text-sm transition-colors ${checked ? "border-red-500 bg-red-50" : "border-red-300 bg-white hover:border-red-400"}`}>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-red-600" data-testid="ricetta-shelf-life-manual-toggle" />
        <span>
          <span className="block font-semibold text-red-700">Scadenza impostata dal cliente</span>
          <span className="mt-0.5 block text-xs text-slate-600">Scavalca il calcolo automatico: tutte le produzioni di questa ricetta useranno i giorni indicati qui sopra, anche dopo l'abbattimento e anche con una categoria del DF Food Safety Expert Engine collegata.</span>
        </span>
      </label>
      {overLimit && (
        <div className="flex items-start gap-2 rounded-xl border-2 border-amber-300 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900" role="alert" data-testid="ricetta-shelf-life-manual-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <span><strong>Scadenza superiore a 5 giorni:</strong> va dimostrata con analisi di laboratorio di shelf life (studio di durabilità del prodotto, Reg. CE 2073/2005, art. 3). Puoi comunque salvare: tieni i referti a disposizione dei controlli.</span>
        </div>
      )}
    </div>
  );
}

function useSleCatalog(enabled) {
  const [catalog, setCatalog] = useState(null);
  useEffect(() => { if (enabled) api.get("/app/shelf-life/catalog").then(({ data }) => setCatalog(data)).catch(() => setCatalog(null)); }, [enabled]);
  return catalog;
}

function SummaryRow({ label, children }) {
  return (
    <div className="grid grid-cols-[8.5rem_1fr] gap-2 py-2 text-sm sm:grid-cols-[11rem_1fr]">
      <dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</dt>
      <dd className="min-w-0 break-words text-navy">{children}</dd>
    </div>
  );
}

function IssuesBox({ issues }) {
  if (!issues.length) {
    return <div className="flex items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 p-3 text-sm font-medium text-emerald-800" data-testid="ricetta-summary-ok"><CheckCircle2 className="h-4 w-4 shrink-0" /> Compilazione completa: nessun dato mancante rilevato.</div>;
  }
  const blocking = issues.some((i) => i.blocking);
  return (
    <div className={`rounded-xl border-2 p-3 ${blocking ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"}`} role="alert" data-testid="ricetta-summary-issues">
      <div className={`mb-1.5 flex items-center gap-2 text-sm font-semibold ${blocking ? "text-red-800" : "text-amber-900"}`}>
        <AlertTriangle className="h-4 w-4 shrink-0" /> {blocking ? "Da completare prima dell'approvazione" : `${issues.length === 1 ? "Un punto da verificare" : `${issues.length} punti da verificare`} — puoi comunque approvare`}
      </div>
      <ul className="space-y-1 text-xs leading-relaxed">
        {issues.map((i, idx) => <li key={idx} className={i.blocking ? "font-semibold text-red-700" : "text-amber-900"}>• {i.text}</li>)}
      </ul>
    </div>
  );
}

function ParamList({ fields, values }) {
  const byKey = Object.fromEntries((fields || []).map((f) => [f.key, f]));
  return (
    <ul className="space-y-0.5 text-xs">
      {Object.entries(values || {}).map(([k, v]) => {
        const f = byKey[k];
        const shown = f?.type === "bool" ? (v ? "sì" : "no") : f?.type === "select" ? (f.options.find(([o]) => o === v)?.[1] || v) : `${v}${f?.unit ? ` ${f.unit}` : ""}`;
        return <li key={k} className="whitespace-pre-line"><span className="text-slate-500">{f?.label || k}:</span> {shown}</li>;
      })}
    </ul>
  );
}

// Modalità suggerita dal software, scelta, valutata e approvata: quattro
// informazioni distinte, mai fuse in un'unica "certificazione".
function PostCookingDecisionRows({ recipe: r }) {
  const suggestion = r.post_cooking_check?.suggestion;
  const assessment = r.haccp_assessment || {};
  return (
    <>
      <SummaryRow label="Suggerita dal software">{suggestion ? DESTINATION_LABELS[suggestion] : <span className="text-slate-500">Nessun suggerimento</span>}</SummaryRow>
      <SummaryRow label="Valutazione HACCP">
        {assessment.motivation ? <><div className="whitespace-pre-line">{assessment.motivation}</div><div className="text-xs text-slate-500">{[assessment.responsible && `Responsabile: ${assessment.responsible}`, assessment.assessed_at && `Data: ${assessment.assessed_at}`, assessment.documentation && `Documenti: ${assessment.documentation}`].filter(Boolean).join(" · ")}</div></>
          : <span className={MODE_BY_KEY[r.post_cooking_destination]?.assessment ? "font-semibold text-red-700" : "text-slate-500"}>{MODE_BY_KEY[r.post_cooking_destination]?.assessment ? "Non documentata (richiesta per approvare)" : "Non documentata"}</span>}
      </SummaryRow>
      <SummaryRow label="Approvazione">{r.status === "approvata" ? `Approvata${r.approved_at ? ` il ${formatItalianDateTime(r.approved_at)}` : ""}` : "Da approvare"}</SummaryRow>
    </>
  );
}

function RecipeApprovalSummary({ recipe: r, sleCatalog, hasShelfLife }) {
  const sleName = (list, id) => (list || []).find((x) => x.id === id)?.name;
  const autoShelfLife = hasShelfLife && !!r.sle_food_category_id;
  const ingredients = r.ingredients || [];
  const shelfLife = r.shelf_life_manual && r.shelf_life_days != null
    ? `${r.shelf_life_days} ${r.shelf_life_days === 1 ? "giorno" : "giorni"} — impostata dal cliente, prevale sul calcolo automatico${r.shelf_life_days > MANUAL_SHELF_LIFE_LAB_DAYS ? ". Oltre 5 giorni: da dimostrare con analisi di laboratorio di shelf life" : ""}`
    : sameDayRecipe(r)
    ? "Stesso giorno di produzione (in giornata)"
    : r.shelf_life_days != null ? `${r.shelf_life_days} ${r.shelf_life_days === 1 ? "giorno" : "giorni"} — valore predefinito della ricetta${autoShelfLife ? ", sostituito dal calcolo del DF Food Safety Expert Engine a ogni abbattimento" : ""}` : "—";
  return (
    <div className="space-y-3" data-testid="ricetta-approval-summary">
      <dl className="divide-y divide-hairline rounded-xl border border-hairline bg-white px-4">
        <SummaryRow label="Ricetta"><strong>{r.name?.toUpperCase()}</strong> <span className="text-xs text-slate-400">v{r.version || 1}</span><div className="text-xs text-slate-500">Prefisso lotto <span className="font-mono">{r.prefix}</span>{r.category ? ` · ${r.category}` : ""}</div></SummaryRow>
        <SummaryRow label={`Ingredienti (${ingredients.length})`}>
          {ingredients.length ? <ul className="space-y-0.5">{ingredients.map((i, idx) => <li key={idx} className="flex justify-between gap-2"><span className="min-w-0 truncate">{i.name || i.ingredient_id}{i.characterizing ? " (QUID)" : ""}</span><span className={`shrink-0 text-xs ${i.qty == null || i.qty === "" ? "font-semibold text-amber-700" : "text-slate-400"}`}>{i.qty == null || i.qty === "" ? "q.tà mancante" : `${i.qty} ${i.unit || ""}`}</span></li>)}</ul> : <span className="font-semibold text-amber-700">Nessuno</span>}
        </SummaryRow>
        <SummaryRow label="Allergeni">
          {r.allergen_labels?.length ? <div className="flex flex-wrap gap-1">{r.allergen_labels.map((a) => <span key={a} className="rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700 ring-1 ring-red-100">{a}</span>)}</div> : <span className="text-slate-500">Nessun allergene rilevato dagli ingredienti</span>}
          {r.trace_labels?.length > 0 && <div className="mt-1 text-xs text-amber-700">Può contenere tracce di: {r.trace_labels.join(", ")}</div>}
        </SummaryRow>
        <SummaryRow label="Cottura">{r.is_cooked ? <>Sì — temperatura al cuore {r.core_temp ?? 75} °C</> : "No, non sarà cotto"}</SummaryRow>
        {r.is_cooked && <SummaryRow label="Dopo la cottura">{r.post_cooking_destination ? DESTINATION_LABELS[r.post_cooking_destination] : <span className="font-semibold text-amber-700">Non indicato (trattato come refrigerato)</span>}</SummaryRow>}
        {r.is_cooked && r.post_cooking_destination && <PostCookingDecisionRows recipe={r} />}
        {r.is_cooked && Object.keys(r.post_cooking_process || {}).length > 0 && <SummaryRow label="Parametri"><ParamList fields={MODE_BY_KEY[r.post_cooking_destination]?.fields} values={r.post_cooking_process} /></SummaryRow>}
        {(r.process_phases || []).length > 0 && <SummaryRow label="Fasi successive"><ol className="list-decimal space-y-0.5 pl-4">{r.process_phases.map((p, i) => <li key={i}><strong>{p.name || `Fase ${i + 1}`}</strong>{p.params ? ` — ${p.params}` : ""}{p.controls ? ` · Controlli: ${p.controls}` : ""}</li>)}</ol></SummaryRow>}
        {!r.is_cooked && <SummaryRow label="Dopo la preparazione">{RAW_BLAST_KEYS.includes(r.post_cooking_destination) ? RAW_DESTINATION_LABELS[r.post_cooking_destination] : RAW_DESTINATION_LABELS.none}</SummaryRow>}
        {!r.is_cooked && r.raw_process?.state && <SummaryRow label="Stato del prodotto">{RAW_STATE_LABELS[r.raw_process.state]}</SummaryRow>}
        <SummaryRow label="Abbattimento">{recipeNeedsBlast(r) ? "Verrà proposta in produzione la registrazione del raffreddamento/abbattimento" : "Non proposto in produzione"}</SummaryRow>
        <SummaryRow label="Conservazione">{r.conservation ? r.conservation : <span className="font-semibold text-red-700">Mancante</span>}</SummaryRow>
        <SummaryRow label="Shelf life">{shelfLife}{r.shelf_life_info?.hours != null && <div className="text-xs text-slate-500">Durata in ore: {r.shelf_life_info.hours}</div>}{r.shelf_life_info?.basis && <div className="text-xs text-slate-500">Motivazione: {r.shelf_life_info.basis}</div>}{r.shelf_life_info?.date_indication && <div className="text-xs text-slate-500">{DATE_INDICATION_LABELS[r.shelf_life_info.date_indication]}</div>}</SummaryRow>
        {FROZEN_KEYS.includes(r.post_cooking_destination) && (r.thaw_hours || r.post_thaw_days) && <SummaryRow label="Scongelamento">{thawInstructions(r.thaw_hours, r.post_thaw_days)}</SummaryRow>}
        {hasShelfLife && (
          <SummaryRow label="Shelf Life Engine">
            {r.sle_food_category_id ? (
              <>
                <div>{sleName(sleCatalog?.categories, r.sle_food_category_id) || "Categoria collegata"}</div>
                <div className="text-xs text-slate-500">
                  {[sleName(sleCatalog?.process_types, r.sle_process_type_id) && `Processo: ${sleName(sleCatalog?.process_types, r.sle_process_type_id)}`,
                    sleName(sleCatalog?.packaging_types, r.sle_packaging_id) && `Imballaggio: ${sleName(sleCatalog?.packaging_types, r.sle_packaging_id)}`,
                    r.sle_frozen_food_category_id && `Se congelato: ${sleName(sleCatalog?.categories, r.sle_frozen_food_category_id) || "categoria collegata"}`].filter(Boolean).join(" · ")}
                </div>
              </>
            ) : <span className="font-semibold text-amber-700">Nessuna categoria collegata</span>}
          </SummaryRow>
        )}
        {r.is_cooked && (r.raw_weight_g != null || r.cooked_weight_g != null) && <SummaryRow label="Pesi">Crudo {r.raw_weight_g ?? "—"} g · Cotto {r.cooked_weight_g ?? "—"} g</SummaryRow>}
      </dl>
      <IssuesBox issues={recipeApprovalIssues(r, { hasShelfLife })} />
    </div>
  );
}

function ApproveRecipeDialog({ recipe, onClose, onEdit, onApproved }) {
  const { isLocked } = useOutletContext();
  const hasShelfLife = !isLocked("shelf_life_expert");
  const sleCatalog = useSleCatalog(hasShelfLife);
  const [busy, setBusy] = useState(false);
  const blocking = recipeApprovalIssues(recipe, { hasShelfLife }).some((i) => i.blocking);
  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/app/catalog/recipes/${recipe.id}/approve`);
      toast.success("Ricetta approvata");
      onApproved(recipe);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-end sm:place-items-start p-0 sm:p-6" data-testid="ricetta-approve-dialog">
      <div className="absolute inset-0 bg-navy/50" onClick={busy ? undefined : onClose} />
      <Card className="relative w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto p-6 rounded-b-none sm:rounded-xl">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading text-xl font-semibold">Verifica prima di approvare</h3>
          <button onClick={onClose} disabled={busy} className="p-2 text-slate-400 hover:text-navy" aria-label="Chiudi"><X className="w-5 h-5" /></button>
        </div>
        <p className="mb-4 text-sm text-slate-500">Ecco cosa hai configurato per questa ricetta. Una volta approvata potrà essere usata in produzione; per cambiarla dopo servirà una nuova versione.</p>
        <RecipeApprovalSummary recipe={recipe} sleCatalog={sleCatalog} hasShelfLife={hasShelfLife} />
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row">
          <Button variant="ghost" className="flex-1" disabled={busy} onClick={onClose}>Annulla</Button>
          <Button variant="outline" className="flex-1" disabled={busy} onClick={onEdit} data-testid="ricetta-approve-dialog-edit"><Pencil className="h-4 w-4" /> Correggi</Button>
          <Button variant="danger" className="flex-1" loading={busy} disabled={blocking} onClick={approve} data-testid="ricetta-approve-dialog-confirm"><CheckCircle2 className="h-4 w-4" /> Approva</Button>
        </div>
      </Card>
    </div>
  );
}

function BulkApproveDialog({ recipes, onClose, onDone }) {
  const { isLocked } = useOutletContext();
  const hasShelfLife = !isLocked("shelf_life_expert");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const sleCatalog = useSleCatalog(hasShelfLife);
  const rows = recipes.map((r) => ({ r, issues: recipeApprovalIssues(r, { hasShelfLife }) }));
  const approvable = rows.filter((x) => !x.issues.some((i) => i.blocking));
  const withWarnings = approvable.filter((x) => x.issues.length).length;
  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/app/catalog/recipes/approve-bulk", { recipe_ids: approvable.map((x) => x.r.id) });
      const approvedCount = data.approved?.length || 0;
      if (approvedCount > 0) toast.success(`${approvedCount} ${approvedCount === 1 ? "ricetta approvata" : "ricette approvate"}`);
      if (data.skipped?.length) toast.error(`${data.skipped.length} non approvate: ${data.skipped.map((s) => `${s.name || s.id} (${s.detail})`).join(", ")}`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-end sm:place-items-start p-0 sm:p-6" data-testid="ricette-bulk-approve-dialog">
      <div className="absolute inset-0 bg-navy/50" onClick={busy ? undefined : onClose} />
      <Card className="relative w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto p-6 rounded-b-none sm:rounded-xl">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading text-xl font-semibold">Verifica prima di approvare ({recipes.length})</h3>
          <button onClick={onClose} disabled={busy} className="p-2 text-slate-400 hover:text-navy" aria-label="Chiudi"><X className="w-5 h-5" /></button>
        </div>
        <p className="mb-4 text-sm text-slate-500">Sintesi di ogni ricetta selezionata. Tocca una ricetta per vedere il dettaglio completo.{rows.length - approvable.length === 1 ? " 1 ricetta non può essere approvata finché non viene completata." : rows.length - approvable.length > 1 ? ` ${rows.length - approvable.length} ricette non possono essere approvate finché non vengono completate.` : ""}</p>
        <div className="space-y-2">
          {rows.map(({ r, issues }) => {
            const blocking = issues.some((i) => i.blocking);
            const open = expanded === r.id;
            return (
              <div key={r.id} className={`rounded-xl border ${blocking ? "border-red-300" : issues.length ? "border-amber-300" : "border-emerald-300"}`}>
                <button type="button" onClick={() => setExpanded(open ? null : r.id)} className="flex w-full items-start gap-3 p-3 text-left" aria-expanded={open}>
                  {blocking ? <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" /> : issues.length ? <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" /> : <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-navy">{r.name?.toUpperCase()}</div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {r.is_cooked ? `Cotto ${r.core_temp ?? 75} °C · ${r.post_cooking_destination ? DESTINATION_LABELS[r.post_cooking_destination] : "gestione dopo cottura non indicata"}` : RAW_BLAST_KEYS.includes(r.post_cooking_destination) ? `Non cotto · ${RAW_DESTINATION_LABELS[r.post_cooking_destination]}` : "Non cotto"}
                      {" · "}{r.conservation || "conservazione mancante"}
                      {" · "}{sameDayRecipe(r) ? "scade in giornata" : r.shelf_life_days != null ? `${r.shelf_life_days} gg` : "shelf life non indicata"}
                    </div>
                    {issues.length > 0 && <div className={`mt-1 text-xs font-medium ${blocking ? "text-red-700" : "text-amber-800"}`}>{issues.length === 1 ? "1 punto" : `${issues.length} punti`} da {blocking ? "completare" : "verificare"}</div>}
                  </div>
                  <ChevronRight className={`mt-0.5 h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
                </button>
                {open && <div className="border-t border-hairline bg-slate-50/60 p-3"><RecipeApprovalSummary recipe={r} sleCatalog={sleCatalog} hasShelfLife={hasShelfLife} /></div>}
              </div>
            );
          })}
        </div>
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row">
          <Button variant="ghost" className="flex-1" disabled={busy} onClick={onClose}>Annulla</Button>
          <Button variant="danger" className="flex-1" loading={busy} disabled={!approvable.length} onClick={approve} data-testid="ricette-bulk-approve-confirm">
            <CheckCircle2 className="h-4 w-4" /> Approva {approvable.length === rows.length ? `tutte (${approvable.length})` : `${approvable.length} su ${rows.length}`}{withWarnings ? ` · ${withWarnings} con avvisi` : ""}
          </Button>
        </div>
      </Card>
    </div>
  );
}

const CORE_TEMP_HELP ="Valore prudenziale predefinito, modificabile in funzione della tipologia di alimento e della procedura HACCP applicabile.";

function PostCookingPicker({ value, onChange, raw = false, check }) {
  if (!raw) {
    return (
      <div className="mt-4 border-t border-amber-200 pt-4" data-testid="ricetta-post-cooking">
        <div className="text-sm font-semibold uppercase tracking-wide text-navy">Come sarà gestito il prodotto dopo la cottura? *</div>
        <p className="mt-1 text-xs text-slate-600">La scelta determina il percorso HACCP in produzione (abbattimento sì/no, controlli pertinenti), la conservazione e la shelf life proposte. L’abbattitore non è richiesto per ogni prodotto cotto.</p>
        <SuggestionBox check={check} value={value} onUse={onChange} />
        <PostCookingCards value={value} onChange={onChange} suggestion={check?.suggestion} />
        {(value === "frozen" || value === "deep_frozen") && (
          <p className="mt-3 rounded-lg border border-indigo-200 bg-white p-3 text-xs leading-relaxed text-indigo-900" data-testid="ricetta-post-cooking-frozen-info">
            <strong>Perché 30 giorni:</strong> {SHELF_LIFE_HELP[value]}{value === "deep_frozen" ? ` ${SURGELATO_INFO}` : ""}
          </p>
        )}
      </div>
    );
  }
  return (
    <div className="mt-4 border-t border-amber-200 pt-4" data-testid="ricetta-raw-storage">
      <div className="text-sm font-semibold uppercase tracking-wide text-navy">Il prodotto viene abbattuto dopo la preparazione? *</div>
      <p className="mt-1 text-xs text-slate-600">Es. semifreddi, creme o basi di pasticceria abbattuti subito e messi in cella negativa: in produzione verrà proposta la registrazione dell’abbattimento e in etichetta comparirà la scadenza del prodotto congelato.</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {RAW_STORAGE_OPTIONS.map((o) => {
          const active = value === o.key;
          return (
            <div key={o.key} className={`relative rounded-xl border-2 transition-all ${active ? "border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200" : `border-hairline bg-white hover:border-indigo-300 ${!value ? "animate-pulse ring-2 ring-amber-300 ring-offset-1" : ""}`}`}>
              <button type="button" onClick={() => onChange(o.key)} className="block w-full p-3 pr-9 text-left" data-testid={`ricetta-raw-storage-${o.key}`} aria-pressed={active}>
                <span className="flex items-start gap-1.5 text-sm font-semibold text-navy">{active && <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" />}{o.label}</span>
                <span className="mt-1 block text-xs leading-relaxed text-slate-500">{o.desc}</span>
              </button>
              {o.help && <span className="absolute right-2 top-2"><FieldHelp text={o.help} /></span>}
            </div>
          );
        })}
      </div>
      {(value === "frozen" || value === "deep_frozen") && (
        <p className="mt-3 rounded-lg border border-indigo-200 bg-white p-3 text-xs leading-relaxed text-indigo-900" data-testid="ricetta-post-cooking-frozen-info">
          <strong>Perché 30 giorni:</strong> {SHELF_LIFE_HELP[value]}{value === "deep_frozen" ? ` ${SURGELATO_INFO}` : ""}
        </p>
      )}
    </div>
  );
}

function RecipeForm({ edit, onClose, onDone }) {
  const { isLocked } = useOutletContext();
  const hasShelfLife = !isLocked("shelf_life_expert");
  const [ings, setIngs] = useState([]);
  const [sleCatalog, setSleCatalog] = useState(null);
  const [f, setF] = useState(edit
    ? { name: edit.name, prefix: edit.prefix, category: edit.category || "", conservation: edit.conservation || "", shelf_life_days: edit.shelf_life_days ?? 2, is_cooked: edit.is_cooked ?? edit.core_temp != null, core_temp: edit.core_temp ?? 75, consumption: edit.consumption || "", cooling: edit.cooling || "",
      sle_food_category_id: edit.sle_food_category_id || "", sle_process_type_id: edit.sle_process_type_id || "", sle_packaging_id: edit.sle_packaging_id || "",
      sle_frozen_food_category_id: edit.sle_frozen_food_category_id || "",
      raw_weight_g: edit.raw_weight_g ?? "", cooked_weight_g: edit.cooked_weight_g ?? "", portion_size_g: edit.portion_size_g ?? "",
      post_cooking_destination: edit.post_cooking_destination || ((edit.is_cooked ?? edit.core_temp != null) === false ? "none" : ""),
      thaw_hours: edit.thaw_hours ?? "", post_thaw_days: edit.post_thaw_days ?? "", is_semi_finished: !!edit.is_semi_finished,
      shelf_life_manual: !!edit.shelf_life_manual,
      post_cooking_process: edit.post_cooking_process || {}, haccp_assessment: edit.haccp_assessment || {},
      shelf_life_info: edit.shelf_life_info || {}, raw_process: edit.raw_process || {}, process_phases: edit.process_phases || [] }
    : { name: "", prefix: "", category: "", conservation: "", shelf_life_days: 2, is_cooked: null, post_cooking_destination: "", core_temp: 75, consumption: "", cooling: "",
      sle_food_category_id: "", sle_process_type_id: "", sle_packaging_id: "", sle_frozen_food_category_id: "",
      raw_weight_g: "", cooked_weight_g: "", portion_size_g: "", thaw_hours: "", post_thaw_days: "", is_semi_finished: false,
      shelf_life_manual: false, post_cooking_process: {}, haccp_assessment: {}, shelf_life_info: {}, raw_process: {}, process_phases: [] });
  const [modeChangeConfirmed, setModeChangeConfirmed] = useState(false);
  const [selected, setSelected] = useState(edit ? (edit.ingredients || []).map((i) => ({ ingredient_id: i.ingredient_id, qty: i.qty ?? "", unit: i.unit || "kg", characterizing: i.characterizing || false })) : []);
  const [ingredientSearch, setIngredientSearch] = useState("");
  const [prefixManual, setPrefixManual] = useState(Boolean(edit));
  const [loading, setLoading] = useState(false);
  const [showWizard, setShowWizard] = useState(null);
  const [sleManualCategory, setSleManualCategory] = useState(false);
  const [createdRecipe, setCreatedRecipe] = useState(null);
  const [approving, setApproving] = useState(false);
  const [showNutrition, setShowNutrition] = useState(false);
  const [showConservationWizard, setShowConservationWizard] = useState(false);

  useEffect(() => { api.get("/app/catalog/ingredients").then(({ data }) => setIngs(data.ingredients)); }, []);
  useEffect(() => { api.get("/app/shelf-life/catalog").then(({ data }) => setSleCatalog(data)).catch(() => setSleCatalog(null)); }, []);

  const manualShelfLife = !!f.shelf_life_manual;
  const autoShelfLife = hasShelfLife && !!f.sle_food_category_id && !manualShelfLife;
  const immediateConsumption = f.is_cooked && f.post_cooking_destination === "immediate" && !manualShelfLife;
  const sameDayAllowed = f.is_cooked && SAME_DAY_KEYS.includes(f.post_cooking_destination);
  const modeChanged = !!edit && f.is_cooked && !!f.post_cooking_destination && (edit.is_cooked ? edit.post_cooking_destination : null) !== f.post_cooking_destination;
  const postCookingCheck = usePostCookingCheck({
    name: f.name, category: f.category, ingredient_ids: selected.map((s) => s.ingredient_id),
    is_cooked: f.is_cooked, post_cooking_destination: f.is_cooked ? f.post_cooking_destination || null : null,
    haccp_assessment: f.haccp_assessment,
  }, !!f.is_cooked);
  const allergenPreview = useMemo(() => {
    const set = new Set();
    selected.forEach((s) => { const ing = ings.find((i) => i.id === s.ingredient_id); (ing?.allergen_labels || []).forEach((a) => set.add(a)); });
    return [...set];
  }, [selected, ings]);

  const addIng = (id) => { if (!id || selected.find((s) => s.ingredient_id === id)) return; setSelected([...selected, { ingredient_id: id, qty: "", unit: ings.find((i) => i.id === id)?.unit || "kg", characterizing: false }]); };
  const removeIng = (id) => setSelected(selected.filter((s) => s.ingredient_id !== id));
  const toggleIng = (id) => selected.some((s) => s.ingredient_id === id) ? removeIng(id) : addIng(id);
  const toggleCharacterizing = (id) => setSelected(selected.map((s) => s.ingredient_id === id ? { ...s, characterizing: !s.characterizing } : s));
  // Il semilavorato collegato a questa stessa ricetta non può esserne ingrediente.
  const ownGroup = edit ? (edit.group_id || edit.id) : null;
  const filteredIngredients = ings.filter((i) => (!ownGroup || i.source_recipe_group_id !== ownGroup) && i.name.toLowerCase().includes(ingredientSearch.trim().toLowerCase()));

  const submit = async () => {
    if (!f.name || !f.prefix) { toast.error("Nome e prefisso obbligatori"); return; }
    if (f.is_cooked === null) { toast.error("Indica se il prodotto sarà sottoposto a cottura"); return; }
    if (f.is_cooked && !f.post_cooking_destination) { toast.error("Indica come sarà gestito il prodotto dopo la cottura"); return; }
    if (f.is_cooked === false && !f.post_cooking_destination) { toast.error("Indica se il prodotto viene abbattuto dopo la preparazione"); return; }
    if (modeChanged && !modeChangeConfirmed) { toast.error("Leggi e conferma il riepilogo del cambio di modalità dopo la cottura"); return; }
    if (!selected.length) { toast.error("Seleziona almeno un ingrediente per creare la ricetta"); return; }
    if (manualShelfLife && f.shelf_life_days === "") { toast.error("Indica i giorni di scadenza impostati dal cliente"); return; }
    if (hasShelfLife && !f.sle_food_category_id && !manualShelfLife) { toast.error("Rispondi alle domande del pannello Shelf Life per collegare una categoria alimentare"); return; }
    setLoading(true);
    try {
      const payload = {
        name: f.name, prefix: f.prefix, category: f.category, conservation: f.conservation,
        consumption: f.consumption, cooling: f.cooling,
        shelf_life_days: f.shelf_life_days === "" ? null : parseInt(f.shelf_life_days),
        shelf_life_manual: manualShelfLife,
        is_cooked: f.is_cooked,
        post_cooking_destination: f.is_cooked ? f.post_cooking_destination : rawDestinationPayload(f.post_cooking_destination),
        thaw_hours: FROZEN_KEYS.includes(f.post_cooking_destination) ? thawPayload(f.thaw_hours) : null,
        post_thaw_days: FROZEN_KEYS.includes(f.post_cooking_destination) ? thawPayload(f.post_thaw_days) : null,
        core_temp: f.is_cooked ? (f.core_temp === "" ? 75 : parseFloat(f.core_temp)) : null,
        raw_weight_g: f.is_cooked && f.raw_weight_g !== "" ? parseFloat(f.raw_weight_g) : null,
        cooked_weight_g: f.is_cooked && f.cooked_weight_g !== "" ? parseFloat(f.cooked_weight_g) : null,
        portion_size_g: f.portion_size_g === "" ? null : parseFloat(f.portion_size_g),
        ingredients: selected.map((s) => ({ ingredient_id: s.ingredient_id, qty: s.qty === "" ? null : parseFloat(s.qty), unit: s.unit, characterizing: !!s.characterizing })),
        is_semi_finished: !!f.is_semi_finished,
        sle_food_category_id: f.sle_food_category_id || null,
        sle_process_type_id: f.sle_process_type_id || null,
        sle_packaging_id: f.sle_packaging_id || null,
        sle_frozen_food_category_id: f.sle_frozen_food_category_id || null,
        post_cooking_process: f.is_cooked ? processPayload(f.post_cooking_destination, f.post_cooking_process) : {},
        haccp_assessment: f.haccp_assessment || {},
        shelf_life_info: { ...(f.shelf_life_info || {}), hours: f.shelf_life_info?.hours === "" || f.shelf_life_info?.hours == null ? null : Number(f.shelf_life_info.hours) },
        raw_process: f.is_cooked ? {} : Object.fromEntries(Object.entries(f.raw_process || {}).filter(([, v]) => v !== "" && v != null).map(([k, v]) => [k, ["proofing_hours", "proofing_temp_c", "fermentation_hours"].includes(k) ? Number(v) : v])),
        process_phases: (f.process_phases || []).filter((p) => (p.name || "").trim() || (p.description || "").trim()),
      };
      let res;
      if (edit) {
        res = await api.post(`/app/catalog/recipes/${edit.id}/new-version`, payload);
        toast.success("Creata nuova versione (bozza). La versione precedente è archiviata.");
      } else {
        res = await api.post("/app/catalog/recipes", payload);
        toast.success("Ricetta creata come bozza.");
      }
      // Ricetta come salvata dal server (allergeni ricalcolati, valori
      // normalizzati): è quella che si riassume prima dell'approvazione.
      const saved = await api.get(`/app/catalog/recipes/${res.data.recipe.id}`).then(({ data }) => data.recipe || data).catch(() => res.data.recipe);
      setCreatedRecipe(saved);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const createdBlocking = !!createdRecipe && recipeApprovalIssues(createdRecipe, { hasShelfLife }).some((i) => i.blocking);
  const approveNow = async () => {
    if (!createdRecipe) return;
    setApproving(true);
    try {
      await api.post(`/app/catalog/recipes/${createdRecipe.id}/approve`);
      toast.success("Ricetta approvata");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setApproving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-end sm:place-items-start p-0 sm:p-6" data-testid="ricetta-modal">
      <div className="absolute inset-0 bg-navy/50" onClick={createdRecipe ? undefined : onClose} />
      <Card className="relative w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto p-6 rounded-b-none sm:rounded-xl">
        {createdRecipe ? (
          <div data-testid="ricetta-approval-step">
            <div className="mb-4 flex items-start gap-3">
              <CheckCircle2 className="h-8 w-8 shrink-0 text-emerald-500" />
              <div>
                <h3 className="font-heading text-xl font-semibold text-navy">Ricetta salvata come bozza</h3>
                <p className="mt-1 text-sm text-slate-500">"{createdRecipe.name}" è stata salvata, ma finché resta in bozza non può essere usata in produzione. Controlla la sintesi qui sotto e approvala ora, oppure rimandala a dopo.</p>
              </div>
            </div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Sintesi di quanto configurato</div>
            <RecipeApprovalSummary recipe={createdRecipe} sleCatalog={sleCatalog} hasShelfLife={hasShelfLife} />
            {createdBlocking && (
              <p className="mt-2 text-xs text-red-700" data-testid="ricetta-conservation-missing-warning">Salva ora con "Approvo più tardi", poi riaprila dalla lista con "Modifica" per completarla.</p>
            )}
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row">
              <Button variant="ghost" className="flex-1" onClick={onDone} data-testid="ricetta-approve-later">Approvo più tardi</Button>
              <Button variant="danger" className="flex-1 animate-pulse ring-2 ring-red-300 ring-offset-1" loading={approving} disabled={createdBlocking} onClick={approveNow} data-testid="ricetta-approve-now"><CheckCircle2 className="h-4 w-4" /> Approva ora</Button>
            </div>
          </div>
        ) : (
        <>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-semibold">{edit ? "Nuova versione ricetta" : "Nuova ricetta"}</h3>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Nome ricetta *"><Input value={f.name} onChange={(e) => { const name = e.target.value.toUpperCase(); setF({ ...f, name, prefix: prefixManual ? f.prefix : makePrefix(name) }); }} placeholder="ES. LASAGNA ALLA BOLOGNESE" data-testid="ricetta-name" /></Field>
          <Field label="Prefisso lotto (generato automaticamente, modificabile)"><Input value={f.prefix} onChange={(e) => { setPrefixManual(true); setF({ ...f, prefix: makePrefix(e.target.value) }); }} placeholder="Es. LASAGNA" data-testid="ricetta-prefix" /></Field>
          <Field label="Categoria (facoltativa)"><Select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="ricetta-category"><option value="">Nessuna categoria</option>{f.category && !RECIPE_CATEGORIES.includes(f.category) && <option value={f.category}>{f.category}</option>}{RECIPE_CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}</Select></Field>
          <Field
            label={manualShelfLife ? "Scadenza impostata dal cliente (giorni dalla produzione) *" : immediateConsumption ? "Shelf life predefinita (giorni) — stesso giorno" : autoShelfLife ? "Shelf life predefinita (giorni) — calcolata automaticamente" : "Shelf life predefinita (giorni)"}
            help={SHELF_LIFE_HELP[f.post_cooking_destination] || SHELF_LIFE_HELP.refrigerated}
            hint={manualShelfLife ? "Giorni decisi dal cliente, contati dal giorno di produzione: valgono per etichette e tracciabilità di ogni lotto." : immediateConsumption ? "Consumo/somministrazione immediata: la scadenza coincide con la data del giorno di produzione." : `${SHELF_LIFE_NOTE}${autoShelfLife ? " Hai collegato una categoria nel pannello DF Food Safety Expert Engine qui sotto: la durata effettiva viene calcolata automaticamente dalla temperatura misurata all'abbattimento." : ""}`}
          ><Input type="number" min={immediateConsumption || sameDayAllowed ? "0" : "1"} value={f.shelf_life_days} placeholder={f.shelf_life_days === "" ? "Da definire" : undefined} disabled={autoShelfLife || immediateConsumption} onChange={(e) => setF({ ...f, shelf_life_days: e.target.value })} data-testid="ricetta-shelf-life" /></Field>
        </div>
        <ManualShelfLifeToggle checked={manualShelfLife} days={f.shelf_life_days} onChange={(on) => setF({ ...f, shelf_life_manual: on })} />
        <ShelfLifeDetails value={f.shelf_life_info} onChange={(v) => setF({ ...f, shelf_life_info: v })} />
        <label className={`mt-3 flex min-h-[44px] cursor-pointer items-start gap-3 rounded-xl border-2 p-3 text-sm transition-colors ${f.is_semi_finished ? "border-violet-400 bg-violet-50" : "border-hairline bg-white hover:border-violet-300"}`} data-testid="ricetta-semi-finished">
          <input type="checkbox" checked={!!f.is_semi_finished} onChange={(e) => setF({ ...f, is_semi_finished: e.target.checked })} className="mt-0.5 h-5 w-5 shrink-0 accent-violet-600" />
          <span><span className="block font-semibold text-navy">Semilavorato: usabile come ingrediente in altre ricette</span><span className="mt-0.5 block text-xs text-slate-600">Per preparazioni fatte da te come besciamella, ragù, impasti o creme. Comparirà tra gli ingredienti delle altre ricette con allergeni e composizione già pronti; in produzione si usa un suo lotto oppure lo si produce al momento.</span></span>
        </label>

        <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50/50 p-4">
          <Field label="Modalità e temperatura di conservazione *"><Input value={f.conservation} onChange={(e) => setF({ ...f, conservation: e.target.value.toUpperCase() })} placeholder="ES. CONSERVARE REFRIGERATO TRA 0 °C E +4 °C" data-testid="ricetta-conservation" /></Field>
          <button type="button" onClick={() => setShowConservationWizard((v) => !v)} className="mt-2 text-xs font-semibold text-indigo-700 underline underline-offset-2" data-testid="ricetta-conservation-wizard-toggle">
            {showConservationWizard ? "Nascondi le domande guidate" : "Non sai cosa scrivere? Fatti guidare"}
          </button>
          {showConservationWizard && (
            <div className="mt-2">
              <ConservationWizard category={f.category} onApply={(text) => { setF({ ...f, conservation: text }); setShowConservationWizard(false); }} />
            </div>
          )}
          <p className="mt-2 text-xs leading-relaxed text-blue-900"><strong>Riferimento:</strong> Reg. (CE) 852/2004, Allegato II, Capitolo IX — conservare gli alimenti a temperature che non comportino rischi per la salute e non interrompere la catena del freddo. Obbligatorio prima di poter approvare la ricetta.</p>
        </div>

        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/50 p-4">
          <div className="text-sm font-semibold text-navy">Il prodotto sarà sottoposto a cottura? *</div>
          <p className="mt-1 text-xs text-slate-600">Se sì, si applica il controllo della temperatura al cuore. Se il prodotto cotto è destinato alla conservazione refrigerata o congelata, oppure il prodotto non cotto viene abbattuto e congelato, il sistema proporrà la registrazione della fase di raffreddamento/abbattimento prevista dal piano HACCP.</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setF({ ...f, is_cooked: true, core_temp: f.core_temp === "" ? 75 : f.core_temp })}
              className={`rounded-xl border-2 p-4 text-left transition-all ${f.is_cooked === true ? "border-orange-500 bg-orange-50 ring-2 ring-orange-200" : `border-hairline bg-white hover:border-orange-300 ${f.is_cooked === null ? "animate-pulse ring-2 ring-amber-300 ring-offset-1" : ""}`}`}
              data-testid="ricetta-cooked-yes"
            >
              <span className="flex items-center gap-2 font-heading font-semibold text-navy"><Flame className="h-5 w-5 text-orange-500" /> Sì, sarà cotto</span>
              <span className="mt-1 block text-xs leading-relaxed text-slate-500">Richiede il controllo della temperatura al cuore; subito dopo indichi come sarà gestito il prodotto.</span>
            </button>
            <button
              type="button"
              onClick={() => setF({ ...f, is_cooked: false, post_cooking_destination: "" })}
              className={`rounded-xl border-2 p-4 text-left transition-all ${f.is_cooked === false ? "border-sky-500 bg-sky-50 ring-2 ring-sky-200" : `border-hairline bg-white hover:border-sky-300 ${f.is_cooked === null ? "animate-pulse ring-2 ring-amber-300 ring-offset-1" : ""}`}`}
              data-testid="ricetta-cooked-no"
            >
              <span className="flex items-center gap-2 font-heading font-semibold text-navy"><Snowflake className="h-5 w-5 text-sky-500" /> No, non sarà cotto</span>
              <span className="mt-1 block text-xs leading-relaxed text-slate-500">Es. prodotti crudi, freschi o preparati senza cottura.</span>
            </button>
          </div>
          {f.is_cooked && <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <Field label="Temperatura al cuore (°C) — valore predefinito" help={CORE_TEMP_HELP}><Input type="number" step="0.1" value={f.core_temp} onChange={(e) => setF({ ...f, core_temp: e.target.value })} data-testid="ricetta-core-temp" /></Field>
            <Field label="Peso crudo totale (g)" hint="Peso della ricetta prima della cottura, per calcolare la resa"><Input type="number" step="1" value={f.raw_weight_g} onChange={(e) => setF({ ...f, raw_weight_g: e.target.value })} data-testid="ricetta-raw-weight" /></Field>
            <Field label="Peso cotto totale (g)" hint="Peso realmente misurato dopo la cottura"><Input type="number" step="1" value={f.cooked_weight_g} onChange={(e) => setF({ ...f, cooked_weight_g: e.target.value })} data-testid="ricetta-cooked-weight" /></Field>
          </div>}
          {f.is_cooked && <PostCookingPicker value={f.post_cooking_destination} check={postCookingCheck} onChange={(key) => setF(withPostCookingDefaults(f, key))} />}
          {f.is_cooked && <ModeChangeImpact recipe={edit} mode={f.post_cooking_destination} process={f.post_cooking_process} isCooked={f.is_cooked} confirmed={modeChangeConfirmed} onConfirm={setModeChangeConfirmed} />}
          {f.is_cooked && f.post_cooking_destination && (
            <PostCookingDetails mode={f.post_cooking_destination}
              process={f.post_cooking_process} onProcess={(v) => setF({ ...f, post_cooking_process: v })}
              assessment={f.haccp_assessment} onAssessment={(v) => setF({ ...f, haccp_assessment: v })}
              phases={f.process_phases} onPhases={(v) => setF({ ...f, process_phases: v })} />
          )}
          {f.is_cooked === false && <PostCookingPicker raw value={f.post_cooking_destination} onChange={(key) => setF(withPostCookingDefaults(f, key))} />}
          {f.is_cooked === false && <RawProcessDetails value={f.raw_process} onChange={(v) => setF({ ...f, raw_process: v })} />}
          {f.is_cooked !== null && FROZEN_KEYS.includes(f.post_cooking_destination) && <ThawFields hours={f.thaw_hours} days={f.post_thaw_days} onChange={(h, d) => setF({ ...f, thaw_hours: h, post_thaw_days: d })} testPrefix="ricetta" />}
        </div>

        <div className="mt-4">
          <div className="flex items-end justify-between gap-3"><div><div className="font-heading font-semibold text-navy">Seleziona gli ingredienti *</div><p className="mt-1 text-xs text-slate-500">Cerca e spunta contemporaneamente tutte le materie prime utilizzate nella ricetta.</p></div><span className="shrink-0 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">{selected.length} selezionati</span></div>
          <div className="relative mt-3"><Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><input type="search" value={ingredientSearch} onChange={(e) => setIngredientSearch(e.target.value)} placeholder="Cerca ingrediente…" className={`${inputCls} pl-10`} data-testid="ricetta-ingredient-search" /></div>
          <div className="mt-2 grid max-h-56 gap-2 overflow-y-auto rounded-xl border border-hairline bg-slate-50 p-3 sm:grid-cols-2" data-testid="ricetta-ingredient-checks">{filteredIngredients.map((i) => { const checked = selected.some((s) => s.ingredient_id === i.id); return <label key={i.id} className={`flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border p-3 text-sm ${checked ? "border-emerald-400 bg-emerald-50 text-emerald-800" : "border-hairline bg-white text-navy"}`}><input type="checkbox" checked={checked} onChange={() => toggleIng(i.id)} className="h-5 w-5 accent-emerald-600" /><span className="font-medium">{i.name}</span>{i.source_recipe_group_id && <SemiBadge />}</label>; })}{filteredIngredients.length === 0 && <p className="col-span-full py-4 text-center text-sm text-slate-500">Nessun ingrediente trovato.</p>}</div>
          <div className="mt-2 space-y-2">
            {selected.map((s) => {
              const ing = ings.find((i) => i.id === s.ingredient_id);
              return (
                <div key={s.ingredient_id} className="flex items-center gap-2">
                  <span className="flex min-w-0 flex-1 items-center gap-2 text-sm text-navy"><span className="truncate">{ing?.name}</span>{ing?.source_recipe_group_id && <SemiBadge />}</span>
                  <label className="flex shrink-0 items-center gap-1.5 text-[11px] text-slate-500" title="Ingrediente caratterizzante (QUID): compare in etichetta con la sua percentuale"><input type="checkbox" checked={!!s.characterizing} onChange={() => toggleCharacterizing(s.ingredient_id)} className="h-4 w-4 accent-emerald-600" /> QUID</label>
                  <input type="number" value={s.qty} placeholder="q.tà" onChange={(e) => setSelected(selected.map((x) => x.ingredient_id === s.ingredient_id ? { ...x, qty: e.target.value } : x))} className={`${inputCls} w-24`} />
                  <span className="text-xs text-slate-400 w-8">{s.unit}</span>
                  <button onClick={() => removeIng(s.ingredient_id)} className="p-2 text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                </div>
              );
            })}
            {selected.length === 0 && <p className="text-sm text-slate-400">Nessun ingrediente. Aggiungine per calcolare gli allergeni.</p>}
          </div>
        </div>

        <div className="mt-3 rounded-lg bg-slate-50 border border-hairline p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Allergeni calcolati automaticamente</div>
          {allergenPreview.length === 0 ? <span className="text-sm text-slate-400">Nessun allergene rilevato</span> : (
            <div className="flex flex-wrap gap-1.5" data-testid="allergen-preview">
              {allergenPreview.map((a) => <span key={a} className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 ring-1 ring-red-100">{a}</span>)}
            </div>
          )}
        </div>

        {hasShelfLife && sleCatalog?.categories?.length > 0 && (
          <div className="mt-3 rounded-xl border-2 border-indigo-300 bg-indigo-50/60 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-navy">DF Food Safety Expert Engine — Shelf Life</span>
              <span className="rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-bold uppercase text-white">Consigliato</span>
            </div>
            <p className="mt-1 text-xs leading-relaxed text-indigo-900">Rispondi alle domande: il motore calcola da solo la scadenza reale in base alla temperatura misurata a ogni abbattimento di questa ricetta, con criterio scientifico e normativo, al posto di una durata fissa impostata a mano.</p>

            <div className="mt-3">
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-indigo-800">Categoria alimentare *</div>
              {f.sle_food_category_id ? (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border-2 border-emerald-300 bg-emerald-50 px-4 py-2.5">
                  <span className="text-sm font-medium text-emerald-900">✓ {sleCatalog.categories.find((c) => c.id === f.sle_food_category_id)?.name || "Categoria collegata"}</span>
                  <button type="button" onClick={() => { setF({ ...f, sle_food_category_id: "" }); setSleManualCategory(false); }} className="text-xs font-medium text-emerald-700 underline underline-offset-2 hover:text-emerald-900" data-testid="ricetta-sle-category-change">Cambia</button>
                </div>
              ) : sleManualCategory ? (
                <div>
                  <div className="flex gap-2">
                    <Select value={f.sle_food_category_id} onChange={(e) => setF({ ...f, sle_food_category_id: e.target.value })} data-testid="ricetta-sle-category">
                      <option value="">Non collegata</option>
                      {sleCatalog.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </Select>
                  </div>
                  <button type="button" onClick={() => setSleManualCategory(false)} className="mt-1.5 text-[11px] text-indigo-700 underline underline-offset-2 hover:text-indigo-900">Torna alle domande guidate</button>
                </div>
              ) : (
                <div className="rounded-lg border border-indigo-200 bg-white p-3">
                  <ShelfLifeCategoryWizard inline onApply={(categoryId) => setF({ ...f, sle_food_category_id: categoryId })} />
                  <button type="button" onClick={() => setSleManualCategory(true)} className="mt-3 text-[11px] text-slate-400 underline underline-offset-2 hover:text-navy">Preferisci scegliere manualmente dall'elenco?</button>
                </div>
              )}
            </div>

            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              <Field label="Tipo di processo">
                <div className="grid gap-1.5 sm:grid-cols-2">
                  {sleCatalog.process_types.map((p) => {
                    const active = f.sle_process_type_id === p.id;
                    return (
                      <button key={p.id} type="button" onClick={() => setF({ ...f, sle_process_type_id: active ? "" : p.id })}
                        className={`rounded-lg border-2 px-3 py-2 text-left text-xs transition-colors ${active ? "border-indigo-500 bg-indigo-100 font-medium text-indigo-900" : "border-hairline bg-white text-navy hover:border-indigo-300"}`}
                        data-testid={`ricetta-sle-process-${p.id}`}
                      >{p.name}</button>
                    );
                  })}
                </div>
              </Field>
              <Field label="Imballaggio">
                <div className="grid gap-1.5 sm:grid-cols-2">
                  {sleCatalog.packaging_types.map((p) => {
                    const active = f.sle_packaging_id === p.id;
                    return (
                      <button key={p.id} type="button" onClick={() => setF({ ...f, sle_packaging_id: active ? "" : p.id })}
                        className={`rounded-lg border-2 px-3 py-2 text-left text-xs transition-colors ${active ? "border-indigo-500 bg-indigo-100 font-medium text-indigo-900" : "border-hairline bg-white text-navy hover:border-indigo-300"}`}
                        data-testid={`ricetta-sle-packaging-${p.id}`}
                      >{p.name}</button>
                    );
                  })}
                </div>
              </Field>
            </div>
            <div className="mt-3 border-t border-indigo-200 pt-3">
              <Field label="Categoria se congelato per conservazione (facoltativa)" hint="Se il prodotto viene talvolta abbattuto con 'Congelamento per conservazione' invece che tenuto in frigo, usa questa categoria al posto di quella sopra per quell'abbattimento.">
                <div className="flex gap-2">
                  <Select value={f.sle_frozen_food_category_id} onChange={(e) => setF({ ...f, sle_frozen_food_category_id: e.target.value })} data-testid="ricetta-sle-frozen-category">
                    <option value="">Non collegata</option>
                    {sleCatalog.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Select>
                  <Button type="button" variant="outline" className="shrink-0 px-3" onClick={() => setShowWizard("frozen")} data-testid="ricetta-sle-frozen-wizard-open">?</Button>
                </div>
              </Field>
            </div>
          </div>
        )}

        <p className="mt-5 mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">Sezioni facoltative</p>

        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50">
          <button type="button" onClick={() => setShowNutrition((v) => !v)} className="flex w-full items-center justify-between gap-3 p-4 text-left" data-testid="ricetta-nutrition-toggle">
            <span className="text-sm font-semibold text-navy">DF Food Safety Expert Engine — Nutritional Analysis</span>
            <ChevronDown className={`h-4 w-4 shrink-0 text-emerald-700 transition-transform ${showNutrition ? "rotate-180" : ""}`} />
          </button>
          {showNutrition && <div className="px-4 pb-4">
            <p className="text-xs leading-relaxed text-emerald-900">Calcola automaticamente la dichiarazione nutrizionale sommando i valori degli ingredienti collegati in Materie prime. Il peso crudo/cotto qui sopra determina la resa di cottura; la porzione qui sotto è facoltativa (in aggiunta al valore per 100 g, sempre obbligatorio).</p>
            <div className="mt-3 sm:w-64"><Field label="Porzione (g), facoltativa"><Input type="number" step="1" value={f.portion_size_g} onChange={(e) => setF({ ...f, portion_size_g: e.target.value })} data-testid="ricetta-portion-size" /></Field></div>
          </div>}
        </div>

        <div className="flex gap-3 mt-5">
          <Button variant="ghost" className="flex-1" onClick={onClose}>Annulla</Button>
          <Button variant="success" className="flex-1" loading={loading} onClick={submit} data-testid="ricetta-submit">Crea ricetta (bozza)</Button>
        </div>
        </>
        )}
      </Card>
      {showWizard && !createdRecipe && (
        <ShelfLifeCategoryWizard
          onClose={() => setShowWizard(null)}
          onApply={(categoryId) => {
            setF({ ...f, ...(showWizard === "frozen" ? { sle_frozen_food_category_id: categoryId } : { sle_food_category_id: categoryId }) });
            setShowWizard(null);
            toast.success("Categoria applicata");
          }}
        />
      )}
    </div>
  );
}

// Ricette NON cotte già approvate (es. semifreddi, mousse, creme di
// pasticceria) che vengono abbattute e messe in cella negativa: invece di
// aprire "Modifica (nuova versione)" una ricetta alla volta, si spuntano qui e
// il server crea per ognuna la nuova versione già approvata.
function BulkRawBlastModal({ recipes, onClose, onDone }) {
  const categories = useMemo(() => [...new Set(recipes.map((r) => r.category || "").filter(Boolean))].sort((a, b) => a.localeCompare(b, "it")), [recipes]);
  const pastry = categories.find((c) => /PASTICCER/i.test(c)) || "";
  const [category, setCategory] = useState(pastry);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [destination, setDestination] = useState("frozen");
  const [shelfLife, setShelfLife] = useState(POST_COOKING_DEFAULT_DAYS.frozen);
  const [conservation, setConservation] = useState(POST_COOKING_CONSERVATION.frozen);
  const [thawHours, setThawHours] = useState("");
  const [postThawDays, setPostThawDays] = useState("");
  const [busy, setBusy] = useState(false);
  const visible = recipes.filter((r) => (!category || (r.category || "") === category) && (!search.trim() || r.name?.toUpperCase().includes(search.trim().toUpperCase())));
  const allVisibleSelected = visible.length > 0 && visible.every((r) => selected.has(r.id));
  const toggle = (id) => setSelected((current) => { const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const toggleVisible = () => setSelected((current) => { const next = new Set(current); visible.forEach((r) => (allVisibleSelected ? next.delete(r.id) : next.add(r.id))); return next; });
  const chooseDestination = (key) => {
    setDestination(key);
    if (Object.values(POST_COOKING_CONSERVATION).includes(conservation)) setConservation(POST_COOKING_CONSERVATION[key]);
  };
  const currentState = (r) => RAW_BLAST_KEYS.includes(r.post_cooking_destination) ? `${RAW_DESTINATION_LABELS[r.post_cooking_destination]} · ${r.shelf_life_days ?? "—"} gg` : `Nessun abbattimento · ${r.shelf_life_days != null ? `${r.shelf_life_days} gg` : "shelf life non indicata"}`;

  const submit = async () => {
    const days = parseInt(shelfLife, 10);
    if (!selected.size) { toast.error("Seleziona almeno una ricetta"); return; }
    if (!days || days < 1) { toast.error("Indica la shelf life in giorni (almeno 1)"); return; }
    if (!conservation.trim()) { toast.error("Indica la modalità di conservazione"); return; }
    if (!window.confirm(`Verrà creata una nuova versione già approvata per ${selected.size} ${selected.size === 1 ? "ricetta" : "ricette"}, con ${RAW_DESTINATION_LABELS[destination].toLowerCase()} e shelf life di ${days} giorni. Le versioni attuali passano in archivio. Confermi?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/app/catalog/recipes/bulk-raw-blast", { recipe_ids: [...selected], post_cooking_destination: destination, shelf_life_days: days, conservation, thaw_hours: thawPayload(thawHours), post_thaw_days: thawPayload(postThawDays) });
      if (data.updated.length) toast.success(`${data.updated.length} ${data.updated.length === 1 ? "ricetta aggiornata" : "ricette aggiornate"}: dalla prossima produzione verrà proposto l’abbattimento`);
      if (data.skipped.length) toast.error(`${data.skipped.length} saltate: ${data.skipped.map((x) => `${x.name || x.id} (${x.detail})`).join(", ")}`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-end sm:place-items-start p-0 sm:p-6" data-testid="ricette-bulk-raw-blast-modal">
      <div className="absolute inset-0 bg-navy/50" onClick={busy ? undefined : onClose} />
      <Card className="relative w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto p-6 rounded-b-none sm:rounded-xl">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading text-xl font-semibold flex items-center gap-2"><Snowflake className="w-5 h-5 text-sky-600" /> Abbattimento in blocco</h3>
          <button onClick={onClose} disabled={busy} className="p-2 text-slate-400 hover:text-navy" aria-label="Chiudi"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-slate-500 font-body">Spunta i prodotti <strong>non cotti</strong> che vengono abbattuti subito dopo la preparazione e messi in cella negativa (es. semifreddi, mousse, creme). Per ognuno viene creata la nuova versione già approvata: in produzione verrà proposta la registrazione dell’abbattimento e in etichetta comparirà la scadenza indicata qui. Ingredienti e allergeni restano invariati.</p>

        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          <div className="relative"><Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca ricetta…" className={`${inputCls} pl-10`} data-testid="ricette-bulk-raw-blast-search" /></div>
          <Select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="ricette-bulk-raw-blast-category"><option value="">Tutte le categorie</option>{categories.map((c) => <option key={c} value={c}>{c}</option>)}</Select>
        </div>
        <div className="mt-2 rounded-xl border border-hairline">
          <label className="flex min-h-[44px] cursor-pointer items-center gap-3 border-b border-hairline bg-slate-50 px-3 text-sm font-semibold text-navy">
            <input type="checkbox" checked={allVisibleSelected} onChange={toggleVisible} disabled={!visible.length} className="h-5 w-5 accent-sky-600" /> Seleziona tutte le visibili
            <span className="ml-auto rounded-full bg-sky-100 px-2.5 py-0.5 text-xs text-sky-800">{selected.size} selezionate</span>
          </label>
          <div className="max-h-64 divide-y divide-hairline overflow-y-auto" data-testid="ricette-bulk-raw-blast-list">
            {visible.map((r) => (
              <label key={r.id} className={`flex min-h-[44px] cursor-pointer items-start gap-3 px-3 py-2.5 ${selected.has(r.id) ? "bg-sky-50" : ""}`}>
                <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} className="mt-0.5 h-5 w-5 shrink-0 accent-sky-600" />
                <span className="min-w-0"><span className="block font-medium text-navy">{r.name?.toUpperCase()}</span><span className="block text-xs text-slate-500">{currentState(r)}</span></span>
              </label>
            ))}
            {!visible.length && <p className="px-3 py-6 text-center text-sm text-slate-400">{recipes.length ? "Nessuna ricetta non cotta approvata con questi filtri." : "Nessuna ricetta non cotta approvata."}</p>}
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-sky-200 bg-sky-50/50 p-4">
          <div className="text-sm font-semibold text-navy">Gestione dopo la preparazione *</div>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {RAW_STORAGE_OPTIONS.filter((o) => RAW_BLAST_KEYS.includes(o.key)).map((o) => (
              <div key={o.key} className={`relative rounded-xl border-2 ${destination === o.key ? "border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200" : "border-hairline bg-white hover:border-indigo-300"}`}>
                <button type="button" onClick={() => chooseDestination(o.key)} className="block w-full p-3 pr-9 text-left" aria-pressed={destination === o.key} data-testid={`ricette-bulk-raw-blast-${o.key}`}>
                  <span className="flex items-start gap-1.5 text-sm font-semibold text-navy">{destination === o.key && <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" />}{o.label}</span>
                </button>
                {o.help && <span className="absolute right-2 top-2"><FieldHelp text={o.help} /></span>}
              </div>
            ))}
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-[10rem_1fr]">
            <Field label="Shelf life (giorni) *" help={SHELF_LIFE_HELP[destination]}><Input type="number" min="1" value={shelfLife} onChange={(e) => setShelfLife(e.target.value)} data-testid="ricette-bulk-raw-blast-shelf-life" /></Field>
            <Field label="Modalità di conservazione *"><Input value={conservation} onChange={(e) => setConservation(e.target.value.toUpperCase())} data-testid="ricette-bulk-raw-blast-conservation" /></Field>
          </div>
          <p className="mt-2 text-xs leading-relaxed text-slate-500">{SHELF_LIFE_NOTE} Se i prodotti hanno durate diverse, ripeti l’operazione per ogni gruppo di prodotti con la stessa durata.</p>
          <ThawFields hours={thawHours} days={postThawDays} onChange={(h, d) => { setThawHours(h); setPostThawDays(d); }} testPrefix="ricette-bulk-raw-blast" />
        </div>

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row">
          <Button variant="ghost" className="flex-1" disabled={busy} onClick={onClose}>Annulla</Button>
          <Button variant="primary" className="flex-1" loading={busy} disabled={!selected.size} onClick={submit} data-testid="ricette-bulk-raw-blast-submit">
            <Snowflake className="h-4 w-4" /> Applica a {selected.size || ""} {selected.size === 1 ? "ricetta" : "ricette"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// Compilazione in blocco: quando più ricette in bozza seguono lo stesso
// processo (es. cotte, abbattute, poi in frigo) le domande guidate della
// scheda singola si rispondono una volta sola e si applicano a tutte.
function BulkCompleteModal({ recipes, onClose, onDone }) {
  const { isLocked } = useOutletContext();
  const hasShelfLife = !isLocked("shelf_life_expert");
  const [sleCatalog, setSleCatalog] = useState(null);
  const [conservation, setConservation] = useState("");
  const [showConservationWizard, setShowConservationWizard] = useState(false);
  const [isCooked, setIsCooked] = useState(null);
  const [coreTemp, setCoreTemp] = useState(75);
  const [destination, setDestination] = useState("");
  const [process, setProcess] = useState({});
  const [assessment, setAssessment] = useState({});
  // Escluse dalla modifica (es. ricetta con farcitura deperibile).
  const [excluded, setExcluded] = useState(new Set());
  const [preview, setPreview] = useState(null);
  // Vuoto = mantiene la shelf life già presente su ogni ricetta: cambiare
  // modalità non sovrascrive mai da solo una durata già impostata.
  const [shelfLife, setShelfLife] = useState("");
  const chooseDestination = (key) => {
    setDestination(key);
    setPreview(null);
    if (!conservation || Object.values(POST_COOKING_CONSERVATION).includes(conservation)) setConservation(POST_COOKING_CONSERVATION[key] || "");
  };
  const prudentialDays = POST_COOKING_DEFAULT_DAYS[destination];
  const immediateConsumption = isCooked && destination === "immediate";
  const [sle, setSle] = useState({ sle_food_category_id: "", sle_process_type_id: "", sle_packaging_id: "" });
  const [sleManualCategory, setSleManualCategory] = useState(false);
  const [busy, setBusy] = useState(null);
  const included = recipes.filter((r) => !excluded.has(r.id));
  const check = usePostCookingCheck({ recipe_ids: recipes.map((r) => r.id), is_cooked: isCooked, post_cooking_destination: isCooked ? destination : null }, !!isCooked && !!destination);
  const checkById = Object.fromEntries((check?.results || []).map((x) => [x.id, x]));
  const toggleExcluded = (id) => { setPreview(null); setExcluded((cur) => { const next = new Set(cur); if (next.has(id)) next.delete(id); else next.add(id); return next; }); };

  useEffect(() => { if (hasShelfLife) api.get("/app/shelf-life/catalog").then(({ data }) => setSleCatalog(data)).catch(() => setSleCatalog(null)); }, [hasShelfLife]);

  const withoutSleCategory = included.filter((r) => !r.sle_food_category_id);
  const sleAvailable = hasShelfLife && sleCatalog?.categories?.length > 0;

  const payload = (approve, previewOnly) => ({
    recipe_ids: included.map((r) => r.id),
    conservation,
    is_cooked: isCooked,
    core_temp: isCooked ? (coreTemp === "" ? 75 : parseFloat(coreTemp)) : null,
    post_cooking_destination: isCooked ? destination : rawDestinationPayload(destination),
    shelf_life_days: shelfLife === "" ? null : parseInt(shelfLife),
    post_cooking_process: isCooked && Object.keys(processPayload(destination, process)).length ? processPayload(destination, process) : null,
    haccp_assessment: (assessment.motivation || "").trim() ? assessment : null,
    sle_food_category_id: sle.sle_food_category_id || null,
    sle_process_type_id: sle.sle_process_type_id || null,
    sle_packaging_id: sle.sle_packaging_id || null,
    approve,
    preview: previewOnly,
  });

  const validate = () => {
    if (!included.length) { toast.error("Tutte le ricette sono escluse: includine almeno una"); return false; }
    if (isCooked === null) { toast.error("Indica se i prodotti saranno sottoposti a cottura"); return false; }
    if (isCooked && !destination) { toast.error("Indica come saranno gestiti i prodotti dopo la cottura"); return false; }
    if (isCooked === false && !destination) { toast.error("Indica se i prodotti vengono abbattuti dopo la preparazione"); return false; }
    if (sleAvailable && !sle.sle_food_category_id && withoutSleCategory.length > 0) {
      toast.error(`Collega una categoria Shelf Life: ${withoutSleCategory.length} ${withoutSleCategory.length === 1 ? "ricetta non ne ha" : "ricette non ne hanno"} ancora una`);
      return false;
    }
    return true;
  };

  const showPreview = async () => {
    if (!validate()) return;
    setBusy("preview");
    try {
      const { data } = await api.post("/app/catalog/recipes/bulk-complete", payload(true, true));
      setPreview(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const submit = async (approve) => {
    if (!validate()) return;
    setBusy(approve ? "approve" : "save");
    try {
      const { data } = await api.post("/app/catalog/recipes/bulk-complete", payload(approve, false));
      const count = (approve ? data.approved : data.updated)?.length || 0;
      if (count > 0) toast.success(`${count} ${count === 1 ? "ricetta" : "ricette"} ${approve ? (count === 1 ? "compilata e approvata" : "compilate e approvate") : (count === 1 ? "compilata" : "compilate")}`);
      if (data.not_approved?.length) toast.error(`${data.not_approved.length} salvate ma rimaste in bozza: ${data.not_approved.map((s) => `${s.name || s.id} (${s.detail})`).join(", ")}`, { duration: 10000 });
      if (data.skipped?.length) toast.error(`${data.skipped.length} saltate: ${data.skipped.map((s) => `${s.name || s.id} (${s.detail})`).join(", ")}`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const changeValue = (c, value) => {
    if (value == null || value === "" || (typeof value === "object" && !Object.keys(value).length)) return "—";
    if (c.field === "post_cooking_destination") return DESTINATION_LABELS[value] || RAW_DESTINATION_LABELS[value] || value;
    if (c.field === "is_cooked") return value ? "Sì" : "No";
    if (c.field === "haccp_assessment") return value.motivation ? "documentata" : "—";
    if (c.field === "post_cooking_process" || c.field === "raw_process") return `${Object.keys(value).length} parametri`;
    const sleList = { sle_food_category_id: sleCatalog?.categories, sle_process_type_id: sleCatalog?.process_types, sle_packaging_id: sleCatalog?.packaging_types }[c.field];
    if (sleList) return sleList.find((x) => x.id === value)?.name || "categoria collegata";
    return String(value);
  };
  const previewRows = preview?.preview || [];
  const approvableCount = previewRows.filter((x) => !x.blockers.length).length;

  const optionBtn = (active) => `rounded-lg border-2 px-3 py-2 text-left text-xs transition-colors ${active ? "border-indigo-500 bg-indigo-100 font-medium text-indigo-900" : "border-hairline bg-white text-navy hover:border-indigo-300"}`;

  return (
    <div className="fixed inset-0 z-50 grid place-items-end sm:place-items-start p-0 sm:p-6" data-testid="ricette-bulk-complete-modal">
      <div className="absolute inset-0 bg-navy/50" onClick={busy ? undefined : onClose} />
      <Card className="relative w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto p-6 rounded-b-none sm:rounded-xl">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading text-xl font-semibold flex items-center gap-2"><Layers className="w-5 h-5 text-navy" /> Compila e approva in blocco</h3>
          <button onClick={onClose} disabled={!!busy} className="p-2 text-slate-400 hover:text-navy" aria-label="Chiudi"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-slate-500 font-body">Le risposte qui sotto verranno applicate alle <strong>{included.length}</strong> ricette incluse. Vengono modificati solo i campi compilati: quelli lasciati vuoti restano come sono su ogni ricetta. Ingredienti, allergeni e codici delle ricette restano sempre invariati. Prima di salvare vedrai l’anteprima delle modifiche.</p>
        <div className="mt-3 max-h-56 divide-y divide-hairline overflow-y-auto rounded-lg border border-hairline bg-slate-50" data-testid="ricette-bulk-complete-list">
          {recipes.map((r) => {
            const c = checkById[r.id];
            const flagged = c?.flags?.length > 0 && ["ambient", "temporary_display"].includes(destination);
            return (
              <label key={r.id} className={`flex min-h-[44px] cursor-pointer items-start gap-3 px-3 py-2 ${excluded.has(r.id) ? "opacity-60" : ""}`}>
                <input type="checkbox" checked={!excluded.has(r.id)} onChange={() => toggleExcluded(r.id)} className="mt-0.5 h-5 w-5 shrink-0 accent-navy" data-testid={`ricette-bulk-include-${r.id}`} />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-navy">{r.name?.toUpperCase()}{excluded.has(r.id) && <span className="text-[10px] font-semibold uppercase text-slate-500">esclusa</span>}</span>
                  {flagged && <span className="mt-0.5 flex items-start gap-1 text-[11px] leading-snug text-red-700" data-testid={`ricette-bulk-warning-${r.id}`}><AlertTriangle className="mt-px h-3 w-3 shrink-0" />Questa ricetta contiene ingredienti che richiedono una valutazione specifica delle condizioni di conservazione{c.flags?.length ? ` (${c.flags.map((f) => f.text).join("; ")})` : ""}. Puoi escluderla, oppure documentare la valutazione.</span>}
                  {c?.suggestion && destination && c.suggestion !== destination && !flagged && <span className="mt-0.5 block text-[11px] text-amber-800">Il software suggerisce «{DESTINATION_LABELS[c.suggestion]}»</span>}
                </span>
              </label>
            );
          })}
        </div>

        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/50 p-4">
          <div className="text-sm font-semibold text-navy">I prodotti saranno sottoposti a cottura? *</div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <button type="button" onClick={() => { setIsCooked(true); setDestination(""); setPreview(null); }}
              className={`rounded-xl border-2 p-4 text-left transition-all ${isCooked === true ? "border-orange-500 bg-orange-50 ring-2 ring-orange-200" : "border-hairline bg-white hover:border-orange-300"}`}
              data-testid="ricette-bulk-cooked-yes">
              <span className="flex items-center gap-2 font-heading font-semibold text-navy"><Flame className="h-5 w-5 text-orange-500" /> Sì, saranno cotti</span>
              <span className="mt-1 block text-xs leading-relaxed text-slate-500">Controllo della temperatura al cuore; subito dopo indichi come saranno gestiti.</span>
            </button>
            <button type="button" onClick={() => { setIsCooked(false); setDestination(""); setPreview(null); }}
              className={`rounded-xl border-2 p-4 text-left transition-all ${isCooked === false ? "border-sky-500 bg-sky-50 ring-2 ring-sky-200" : "border-hairline bg-white hover:border-sky-300"}`}
              data-testid="ricette-bulk-cooked-no">
              <span className="flex items-center gap-2 font-heading font-semibold text-navy"><Snowflake className="h-5 w-5 text-sky-500" /> No, non saranno cotti</span>
              <span className="mt-1 block text-xs leading-relaxed text-slate-500">Es. prodotti crudi, freschi o preparati senza cottura.</span>
            </button>
          </div>
          {isCooked && <div className="mt-3 sm:w-72"><Field label="Temperatura al cuore (°C) — valore predefinito" help={CORE_TEMP_HELP}><Input type="number" step="0.1" value={coreTemp} onChange={(e) => setCoreTemp(e.target.value)} data-testid="ricette-bulk-core-temp" /></Field></div>}
          {isCooked && <PostCookingPicker value={destination} onChange={chooseDestination} />}
          {isCooked && destination && (
            <PostCookingDetails mode={destination} testPrefix="ricette-bulk"
              process={process} onProcess={(v) => { setProcess(v); setPreview(null); }}
              assessment={assessment} onAssessment={(v) => { setAssessment(v); setPreview(null); }} />
          )}
          {isCooked && destination && <p className="mt-2 text-[11px] text-slate-500">Parametri e valutazione compilati qui sostituiscono quelli di ogni ricetta inclusa; se li lasci vuoti restano quelli già presenti (solo se la modalità non cambia).</p>}
          {isCooked === false && <PostCookingPicker raw value={destination} onChange={chooseDestination} />}
        </div>

        <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50/50 p-4">
          <div className="mb-3 sm:w-72">
            <Field
              label={immediateConsumption ? "Shelf life predefinita (giorni) — stesso giorno" : "Shelf life (giorni)"}
              help={SHELF_LIFE_HELP[destination] || SHELF_LIFE_HELP.refrigerated}
              hint={immediateConsumption ? "La scadenza coincide con la data del giorno di produzione." : `${SHELF_LIFE_NOTE}${shelfLife === "" ? " Vuoto = resta la shelf life già presente su ogni ricetta." : ""}`}
            ><Input type="number" min={isCooked && SAME_DAY_KEYS.includes(destination) ? "0" : "1"} value={shelfLife} disabled={immediateConsumption} onChange={(e) => { setShelfLife(e.target.value); setPreview(null); }} placeholder="Invariata" data-testid="ricette-bulk-shelf-life" /></Field>
            {!immediateConsumption && prudentialDays != null && shelfLife === "" && destination && (
              <button type="button" onClick={() => setShelfLife(String(prudentialDays))} className="mt-1.5 text-xs font-semibold text-indigo-700 underline underline-offset-2" data-testid="ricette-bulk-shelf-life-default">Applica a tutte il valore prudenziale di {prudentialDays} {prudentialDays === 1 ? "giorno" : "giorni"}</button>
            )}
          </div>
          <Field label="Modalità e temperatura di conservazione" hint="Vuoto = resta la dicitura già presente su ogni ricetta (senza dicitura la ricetta non può essere approvata)."><Input value={conservation} onChange={(e) => { setConservation(e.target.value.toUpperCase()); setPreview(null); }} placeholder="ES. CONSERVARE IN FRIGORIFERO A TEMPERATURA CONTROLLATA (0/+4 °C)" data-testid="ricette-bulk-conservation" /></Field>
          <button type="button" onClick={() => setShowConservationWizard((v) => !v)} className="mt-2 text-xs font-semibold text-indigo-700 underline underline-offset-2">
            {showConservationWizard ? "Nascondi le domande guidate" : "Non sai cosa scrivere? Fatti guidare"}
          </button>
          {showConservationWizard && (
            <div className="mt-2">
              <ConservationWizard onApply={(text) => { setConservation(text); setShowConservationWizard(false); }} />
            </div>
          )}
        </div>

        {sleAvailable && (
          <div className="mt-3 rounded-xl border-2 border-indigo-300 bg-indigo-50/60 p-4">
            <div className="text-sm font-semibold text-navy">DF Food Safety Expert Engine — Shelf Life</div>
            <p className="mt-1 text-xs leading-relaxed text-indigo-900">
              {withoutSleCategory.length === 0
                ? "Tutte le ricette selezionate hanno già una categoria collegata: lasciala vuota per mantenerle, oppure scegline una per sostituirla su tutte."
                : `${withoutSleCategory.length} ${withoutSleCategory.length === 1 ? "ricetta non ha" : "ricette non hanno"} ancora una categoria: scegline una da applicare a tutte le selezionate.`}
            </p>
            <div className="mt-3">
              {sle.sle_food_category_id ? (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border-2 border-emerald-300 bg-emerald-50 px-4 py-2.5">
                  <span className="text-sm font-medium text-emerald-900">✓ {sleCatalog.categories.find((c) => c.id === sle.sle_food_category_id)?.name || "Categoria collegata"}</span>
                  <button type="button" onClick={() => { setSle({ ...sle, sle_food_category_id: "" }); setSleManualCategory(false); }} className="text-xs font-medium text-emerald-700 underline underline-offset-2">Cambia</button>
                </div>
              ) : sleManualCategory ? (
                <div>
                  <Select value={sle.sle_food_category_id} onChange={(e) => setSle({ ...sle, sle_food_category_id: e.target.value })} data-testid="ricette-bulk-sle-category">
                    <option value="">Non collegata</option>
                    {sleCatalog.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Select>
                  <button type="button" onClick={() => setSleManualCategory(false)} className="mt-1.5 text-[11px] text-indigo-700 underline underline-offset-2">Torna alle domande guidate</button>
                </div>
              ) : (
                <div className="rounded-lg border border-indigo-200 bg-white p-3">
                  <ShelfLifeCategoryWizard inline onApply={(categoryId) => setSle({ ...sle, sle_food_category_id: categoryId })} />
                  <button type="button" onClick={() => setSleManualCategory(true)} className="mt-3 text-[11px] text-slate-400 underline underline-offset-2 hover:text-navy">Preferisci scegliere manualmente dall'elenco?</button>
                </div>
              )}
            </div>
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              <Field label="Tipo di processo (facoltativo)">
                <div className="grid gap-1.5">
                  {sleCatalog.process_types.map((p) => <button key={p.id} type="button" onClick={() => setSle({ ...sle, sle_process_type_id: sle.sle_process_type_id === p.id ? "" : p.id })} className={optionBtn(sle.sle_process_type_id === p.id)}>{p.name}</button>)}
                </div>
              </Field>
              <Field label="Imballaggio (facoltativo)">
                <div className="grid gap-1.5">
                  {sleCatalog.packaging_types.map((p) => <button key={p.id} type="button" onClick={() => setSle({ ...sle, sle_packaging_id: sle.sle_packaging_id === p.id ? "" : p.id })} className={optionBtn(sle.sle_packaging_id === p.id)}>{p.name}</button>)}
                </div>
              </Field>
            </div>
          </div>
        )}

        {preview && (
          <div className="mt-4 rounded-xl border-2 border-navy/30 bg-white p-4" data-testid="ricette-bulk-preview">
            <div className="font-heading text-lg font-semibold text-navy">Anteprima delle modifiche</div>
            <p className="mt-1 text-xs text-slate-500">Per ogni ricetta: i campi che verranno modificati (valore attuale → nuovo). Tutto il resto resta invariato.</p>
            <div className="mt-3 space-y-2">
              {previewRows.map((x) => (
                <div key={x.id} className={`rounded-lg border p-3 text-xs ${x.blockers.length ? "border-amber-300 bg-amber-50/60" : "border-hairline"}`} data-testid={`ricette-bulk-preview-${x.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-semibold text-navy">{x.name?.toUpperCase()}</span>
                    <button type="button" onClick={() => toggleExcluded(x.id)} className="shrink-0 text-[11px] font-semibold text-slate-500 underline underline-offset-2">Escludi</button>
                  </div>
                  {x.changes.length ? <ul className="mt-1 space-y-0.5">{x.changes.map((c) => <li key={c.field}><span className="text-slate-500">{c.label}:</span> {changeValue(c, c.from)} → <strong>{changeValue(c, c.to)}</strong></li>)}</ul> : <p className="mt-1 text-slate-500">Nessuna modifica.</p>}
                  {x.notes.map((n) => <p key={n} className="mt-1 text-amber-800">• {n}</p>)}
                  {x.warnings.map((w) => <p key={w} className="mt-1 font-medium text-red-700">• {w}</p>)}
                  {x.blockers.length > 0 && <p className="mt-1 font-semibold text-amber-900">Verrà salvata ma resterà in bozza: {x.blockers.join("; ")}.</p>}
                </div>
              ))}
              {preview.skipped?.map((x) => <p key={x.id} className="text-xs text-slate-500">• {x.name || x.id}: saltata ({x.detail})</p>)}
            </div>
          </div>
        )}

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row">
          <Button variant="ghost" className="flex-1" disabled={!!busy} onClick={onClose}>Annulla</Button>
          {!preview ? (
            <Button variant="primary" className="flex-1" loading={busy === "preview"} disabled={!!busy || !included.length} onClick={showPreview} data-testid="ricette-bulk-preview-open">Anteprima delle modifiche ({included.length})</Button>
          ) : (
            <>
              <Button variant="outline" className="flex-1" loading={busy === "save"} disabled={!!busy} onClick={() => submit(false)} data-testid="ricette-bulk-save">Salva senza approvare</Button>
              <Button variant="danger" className="flex-1" loading={busy === "approve"} disabled={!!busy} onClick={() => submit(true)} data-testid="ricette-bulk-approve"><CheckCircle2 className="h-4 w-4" /> Salva e approva {approvableCount === previewRows.length ? `(${approvableCount})` : `${approvableCount} su ${previewRows.length}`}</Button>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}

// Import massivo ricette da CSV/XLSX (altri gestionali HACCP): stesso schema
// "preview poi confirm" già usato per l'import materie prime in
// Ingredienti.jsx (ImportModal), qui esteso con rilevamento/mapping colonne
// e gestione duplicati per nome ricetta.
function RecipeImportModal({ onClose, onDone }) {
  const [step, setStep] = useState("upload"); // upload | mapping | preview | result
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({ name_column: null, ingredients_column: null });
  const [duplicateActions, setDuplicateActions] = useState({});
  const [result, setResult] = useState(null);

  const downloadTemplate = (format) => downloadServerFile("/app/catalog/recipes/import-template", { format }, `modello_ricette.${format}`);

  const runPreview = async (chosenFile, explicitMapping) => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", chosenFile);
      if (explicitMapping?.name_column != null) fd.append("name_column", explicitMapping.name_column);
      if (explicitMapping?.ingredients_column != null) fd.append("ingredients_column", explicitMapping.ingredients_column);
      const { data } = await api.post("/app/catalog/recipes/import-preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      if (!data.confident) {
        setMapping({ name_column: data.name_column, ingredients_column: data.ingredients_column });
        setStep("mapping");
      } else {
        const defaults = {};
        data.rows.forEach((r) => { if (r.duplicate) defaults[r.row] = "skip"; });
        setDuplicateActions(defaults);
        setStep("preview");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const onFile = (e) => {
    const chosen = e.target.files?.[0];
    if (!chosen) return;
    setFile(chosen);
    runPreview(chosen);
  };

  const confirmMapping = () => {
    if (mapping.name_column == null || mapping.ingredients_column == null) { toast.error("Seleziona entrambe le colonne"); return; }
    if (mapping.name_column === mapping.ingredients_column) { toast.error("Le due colonne devono essere diverse"); return; }
    runPreview(file, mapping);
  };

  const setRowDuplicateAction = (row, action) => setDuplicateActions((cur) => ({ ...cur, [row]: action }));
  const applyToAllDuplicates = (action) => {
    const next = { ...duplicateActions };
    preview.rows.filter((r) => r.duplicate).forEach((r) => { next[r.row] = action; });
    setDuplicateActions(next);
  };

  const confirmImport = async () => {
    const rows = preview.rows.filter((r) => r.valid).map((r) => ({
      name: r.name,
      ingredient_names: r.ingredients.map((i) => i.name),
      duplicate_action: r.duplicate ? (duplicateActions[r.row] || "skip") : "new",
      duplicate_recipe_id: r.duplicate_recipe_id,
    }));
    if (rows.length === 0) { toast.error("Nessuna riga valida da importare"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/app/catalog/recipes/import-confirm", { rows });
      setResult(data);
      setStep("result");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const invalidRows = preview?.rows.filter((r) => !r.valid) || [];
  const duplicateRows = preview?.rows.filter((r) => r.duplicate) || [];
  const duplicateInFileRows = preview?.rows.filter((r) => r.duplicate_in_file) || [];
  const newIngredientsCount = preview
    ? new Set(preview.rows.flatMap((r) => r.ingredients.filter((i) => !i.exists).map((i) => i.name.toLowerCase()))).size
    : 0;

  return (
    <div className="fixed inset-0 z-50 grid place-items-end sm:place-items-start p-0 sm:p-6" data-testid="recipe-import-modal">
      <div className="absolute inset-0 bg-navy/50" onClick={onClose} />
      <Card className="relative max-h-[100dvh] w-full overflow-y-auto rounded-b-none p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:max-h-[92vh] sm:max-w-3xl sm:rounded-xl sm:p-6">
        <div className="mb-4 flex min-w-0 items-start justify-between gap-2">
          <h3 className="min-w-0 font-heading text-xl font-semibold">Importa ricette da file</h3>
          <button onClick={onClose} className="grid h-11 w-11 shrink-0 place-items-center rounded-lg text-slate-400 hover:text-navy" aria-label="Chiudi importazione"><X className="w-5 h-5" /></button>
        </div>

        {step === "upload" && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Carica un file CSV o Excel con le tue ricette: per ognuna basta il nome e l'elenco degli ingredienti (separati da ";" o ","). Il sistema riconosce automaticamente le colonne più comuni, anche da altri gestionali.</p>
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" onClick={() => downloadTemplate("xlsx")} data-testid="recipe-import-template-xlsx"><Download className="w-4 h-4" /> Scarica modello Excel</Button>
              <Button variant="outline" onClick={() => downloadTemplate("csv")} data-testid="recipe-import-template-csv"><Download className="w-4 h-4" /> Scarica modello CSV</Button>
            </div>
            <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg bg-navy px-5 py-2.5 text-sm text-white" data-testid="recipe-import-file-label">
              <Upload className="w-4 h-4" /> Scegli file .csv o .xlsx
              <input type="file" accept=".csv,.xlsx,.xlsm" onChange={onFile} className="hidden" data-testid="recipe-import-file" />
            </label>
            {loading && <Spinner />}
          </div>
        )}

        {step === "mapping" && preview && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Non sono riuscito a riconoscere con certezza le colonne giuste: indicale tu. Intestazioni trovate nel file:</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Colonna con il nome della ricetta">
                <Select value={mapping.name_column ?? ""} onChange={(e) => setMapping((m) => ({ ...m, name_column: e.target.value === "" ? null : Number(e.target.value) }))} data-testid="recipe-import-map-name">
                  <option value="">Seleziona colonna</option>
                  {preview.headers.map((h, idx) => <option key={idx} value={idx}>{h || `Colonna ${idx + 1}`}</option>)}
                </Select>
              </Field>
              <Field label="Colonna con gli ingredienti">
                <Select value={mapping.ingredients_column ?? ""} onChange={(e) => setMapping((m) => ({ ...m, ingredients_column: e.target.value === "" ? null : Number(e.target.value) }))} data-testid="recipe-import-map-ingredients">
                  <option value="">Seleziona colonna</option>
                  {preview.headers.map((h, idx) => <option key={idx} value={idx}>{h || `Colonna ${idx + 1}`}</option>)}
                </Select>
              </Field>
            </div>
            <div className="flex flex-col-reverse gap-3 sm:flex-row">
              <Button variant="ghost" className="flex-1" onClick={() => setStep("upload")}>Indietro</Button>
              <Button variant="success" className="flex-1" loading={loading} onClick={confirmMapping} data-testid="recipe-import-map-confirm">Continua</Button>
            </div>
          </div>
        )}

        {step === "preview" && preview && (
          <div className="space-y-4">
            <div className="text-sm text-slate-500 font-body">
              <strong>{preview.total}</strong> ricette rilevate · <strong className="text-emerald-600">{preview.importable}</strong> pronte per l'importazione
              {invalidRows.length > 0 && <> · <strong className="text-red-600">{invalidRows.length}</strong> richiedono verifica</>}
              {newIngredientsCount > 0 && <div className="mt-1 text-xs text-blue-700">{newIngredientsCount} nuovi ingredienti verranno creati automaticamente nell'anagrafica materie prime.</div>}
            </div>

            {duplicateInFileRows.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <strong>{duplicateInFileRows.length} ricette</strong> compaiono più volte nel file con lo stesso nome. Verranno importate tutte come ricette separate: controllale dopo l'importazione dalla lista (puoi usare Modifica o Elimina su ciascuna).
              </div>
            )}

            {duplicateRows.length > 0 && (
              <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <div className="text-sm font-medium text-amber-800">{duplicateRows.length} ricette risultano già presenti per nome. Cosa vuoi fare?</div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" className="h-auto px-3 py-1.5 text-xs" onClick={() => applyToAllDuplicates("skip")}>Salta tutte</Button>
                  <Button variant="outline" className="h-auto px-3 py-1.5 text-xs" onClick={() => applyToAllDuplicates("replace")}>Sostituisci tutte (nuova versione)</Button>
                  <Button variant="outline" className="h-auto px-3 py-1.5 text-xs" onClick={() => applyToAllDuplicates("new")}>Importa tutte come nuove</Button>
                </div>
              </div>
            )}

            <div className="overflow-hidden rounded-lg border border-hairline">
              <div className="max-h-96 divide-y divide-hairline overflow-y-auto">
                {preview.rows.map((r) => (
                  <div key={r.row} className="p-3 text-sm" data-testid={`recipe-import-row-${r.row}`}>
                    <div className="flex items-start gap-3">
                      {r.valid ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />}
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-navy">{r.name || <span className="text-red-500">— nome mancante —</span>}</div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {r.ingredients.map((ing, i) => (
                            <span key={i} className={`rounded px-1.5 py-0.5 text-[10px] ${ing.exists ? "bg-slate-100 text-slate-600" : "bg-blue-50 text-blue-700"}`}>{ing.name}{!ing.exists && " (nuovo)"}</span>
                          ))}
                        </div>
                        {r.errors.length > 0 && <div className="mt-1 text-[11px] text-red-500">{r.errors.join(" · ")}</div>}
                        {r.duplicate_in_file && <div className="mt-1 text-[11px] font-medium text-red-600">Nome ripetuto più volte nel file — verrà importata comunque, verificala dopo</div>}
                        {r.duplicate && (
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className="text-[11px] text-amber-700">Già presente:</span>
                            {["skip", "replace", "new"].map((action) => (
                              <button key={action} type="button" onClick={() => setRowDuplicateAction(r.row, action)}
                                className={`rounded-full border px-2 py-1 text-[11px] ${(duplicateActions[r.row] || "skip") === action ? "border-navy bg-navy text-white" : "border-hairline text-slate-500"}`}>
                                {action === "skip" ? "Salta" : action === "replace" ? "Sostituisci" : "Importa come nuova"}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 sm:flex-row">
              <Button variant="ghost" className="flex-1" onClick={onClose}>Annulla</Button>
              <Button variant="success" className="flex-1" loading={loading} onClick={confirmImport} data-testid="recipe-import-confirm">Importa ricette</Button>
            </div>
          </div>
        )}

        {step === "result" && result && (
          <div className="space-y-4">
            <div className="rounded-lg bg-emerald-50 p-4 text-emerald-800 ring-1 ring-emerald-200">
              <div className="font-heading text-lg font-semibold">Importazione completata</div>
              <div className="mt-1 text-sm">
                {result.created} {result.created === 1 ? "ricetta creata" : "ricette create"}
                {result.replaced > 0 && <>, {result.replaced} sostituit{result.replaced === 1 ? "a" : "e"} (nuova versione)</>}
                {result.skipped > 0 && <>, {result.skipped} saltat{result.skipped === 1 ? "a" : "e"}</>}
              </div>
            </div>
            {result.errors.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                <div className="mb-1 font-medium">Dettaglio righe non importate:</div>
                <ul className="space-y-1 text-xs">
                  {result.errors.map((e, i) => <li key={i}>• <strong>{e.name}</strong>: {e.detail}</li>)}
                </ul>
              </div>
            )}
            <Button variant="success" className="w-full" onClick={onDone} data-testid="recipe-import-done">Chiudi</Button>
          </div>
        )}
      </Card>
    </div>
  );
}

function SemiBadge() {
  return <span className="shrink-0 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700" title="Preparazione interna: una tua ricetta usata come ingrediente">Semilavorato</span>;
}
